import { create } from 'zustand';
import * as THREE from 'three';
import { DanceState, Pose, Keyframe, CameraState, ActingSource, TorsoInteractionParams, JointRotation, SavedSequence, SoundtrackModalContext } from '../types';
import { JOINT_CONSTRAINTS, getJointRotationOrder, clamp } from '../utils/constraints';
import { MANNEQUIN_DIMENSIONS } from '../utils/dimensions';
import { analyzeAudio, decodeAudioFile, calculateFileHash } from '../utils/audioAnalyzer';
import { exportData, importData } from '../utils/fileSystem';
import { trackEditorEvent, trackSequenceEvent, trackPoseEvent, trackTimelineEvent, trackEvent } from '../lib/analytics';
import { useStorageStore } from './useStorageStore';
import i18n from 'i18next';
import { fetchWithAuth } from '../lib/api';

const PROXY_URL = import.meta.env.VITE_MUSIC_PROXY_URL as string;

function extractFilename(url: string): string {
  try {
    const pathname = new URL(url).pathname;
    const segment = pathname.split('/').filter(Boolean).pop() ?? '';
    if (segment && /\.\w{2,5}$/.test(segment)) return decodeURIComponent(segment);
  } catch {
    // ignore
  }
  return 'soundtrack.mp3';
}

export function getFriendlyErrorMessage(err: Error, t: (key: string, opts?: any) => string): string {
  const msg = err.message;
  if (msg === 'ACCESS_DENIED') {
    return t('music.url_fetch_failed_message_403');
  }
  if (msg === 'NOT_FOUND') {
    return t('music.url_fetch_failed_message_404');
  }
  if (msg === 'LIMIT_EXCEEDED') {
    return t('music.url_fetch_failed_message_413');
  }
  if (msg === 'INVALID_AUDIO_FORMAT') {
    return t('music.url_fetch_failed_message_invalid_format', {
      defaultValue: 'The downloaded file is not a valid audio format. Ensure the sharing link points directly to the audio file.'
    });
  }
  if (msg === 'HASH_MISMATCH') {
    return t('library.incorrect_music_message');
  }
  return t('music.url_fetch_failed_message_generic');
}

// Initial default pose (T-Pose / relaxed slightly)
export const INITIAL_POSE: Pose = {
  neck: [0, 0, 0],
  head: [0, 0, 0],
  stomach: [0, 0, 0],
  chest: [0, 0, 0],
  torso: [0, 0, 0],
  leftClavicle: [0, 0, -20],
  rightClavicle: [0, 0, 20],
  leftShoulder: [0, 0, 20],
  rightShoulder: [0, 0, -20],
  leftElbow: [0, 0, 0], // X only
  rightElbow: [0, 0, 0], // X only
  pelvis: [0, 0, 0],
  leftHip: [0, 0, 0],
  rightHip: [0, 0, 0],
  leftKnee: [0, 0, 0], // X only
  rightKnee: [0, 0, 0], // X only
  leftPalm: [0, 0, 0],
  rightPalm: [0, 0, 0],
  leftFoot: [0, 0, 0],
  rightFoot: [0, 0, 0],
  elevation: [0, 0, 0], // elevation[1] controls vertical offset
  orientation: [0, 0, 0], // orientation[0,1,2] controls global Pitch, Yaw, Roll
};

// Calculate elevation needed to touch the floor based on current joint angles
// Reusable objects for calculateGroundedElevation to reduce GC pressure
const _m1 = new THREE.Matrix4();
const _m2 = new THREE.Matrix4();
const _m3 = new THREE.Matrix4();
const _m4 = new THREE.Matrix4();
const _m5 = new THREE.Matrix4();
const _m6 = new THREE.Matrix4();
const _m7 = new THREE.Matrix4();
const _v1 = new THREE.Vector3();
const _v2 = new THREE.Vector3();
const _v3 = new THREE.Vector3();
const _e1 = new THREE.Euler();
const _mOrient = new THREE.Matrix4();
const _eOrient = new THREE.Euler();
const _vTorsoBase = new THREE.Vector3();

// Reusable objects for updateTorsoRotation
const _tm1 = new THREE.Matrix4();
const _tv1 = new THREE.Vector3();
const _tv2 = new THREE.Vector3();
const _tv3 = new THREE.Vector3();

const getRotM = (pose: Pose, joint: string, target: THREE.Matrix4) => {
  const angles = (pose[joint] as JointRotation) || [0, 0, 0];
  _e1.set(
    THREE.MathUtils.degToRad(angles[0]),
    THREE.MathUtils.degToRad(angles[1]),
    THREE.MathUtils.degToRad(angles[2]),
    getJointRotationOrder(joint) as any
  );
  return target.makeRotationFromEuler(_e1);
};

export const calculateGroundedElevation = (pose: Pose, dimensions = MANNEQUIN_DIMENSIONS): number => {
  // Dimensions from parameters/defaults
  const torsoBaseY = dimensions.torsoBase[1]; 
  const upperLegLen = dimensions.upperLegLen;
  const lowerLegLen = dimensions.lowerLegLen;
  const hipOffset = dimensions.hipOffset;
  const stomachHeight = dimensions.stomachHeight;
  const chestOffset = dimensions.chestOffset;
  const torsoHeight = dimensions.torsoHeight;
  const neckLen = dimensions.neckLen;
  const upperArmLen = dimensions.upperArmLen;
  const lowerArmLen = dimensions.lowerArmLen;
  const shoulderWidth = dimensions.shoulderWidth;
  const palmDim = dimensions.palmDim;
  const headDim = dimensions.headDim;
  
  let localMinY = 0;

  // Use _m3 as mNeckRoot to be reused later
  const mNeckRoot = _m3;

  // Global whole-body orientation (see pose.orientation / the "A"/"F" controls). The
  // pelvis chain below is entirely local to the pelvis; premultiplying orientation
  // onto it makes every downstream joint point (feet, hands, head, ...) reflect its
  // true rotated world position, so the lowest point is found correctly even when
  // the pose is tilted or fully inverted (e.g. head near the floor instead of feet).
  const orientAngles = pose.orientation || [0, 0, 0];
  _eOrient.set(
    THREE.MathUtils.degToRad(orientAngles[0]),
    THREE.MathUtils.degToRad(orientAngles[1]),
    THREE.MathUtils.degToRad(orientAngles[2]),
    'XYZ'
  );
  const mOrient = _mOrient.makeRotationFromEuler(_eOrient);

  // Lower Body (Legs/Feet)
  const mPelvis = getRotM(pose, 'pelvis', _m7).premultiply(mOrient); // pelvis tilt rotates both hip attachment points
  ['left', 'right'].forEach(side => {
    const dir = side === 'left' ? 1 : -1;
    const mHip = _m4.copy(mPelvis).multiply(_m1.makeTranslation(hipOffset[0] * dir, hipOffset[1], hipOffset[2])).multiply(getRotM(pose, side + 'Hip', _m5));
    
    _v1.set(0, -upperLegLen, 0).applyMatrix4(mHip);
    localMinY = Math.min(localMinY, _v1.y - 0.2); 

    const mKnee = _m6.copy(mHip).multiply(_m1.makeTranslation(0, -upperLegLen, 0)).multiply(getRotM(pose, side + 'Knee', _m2));
    
    _v1.set(0, -lowerLegLen, 0).applyMatrix4(mKnee);
    const footJointRadius = side === 'left' ? 0.12 : 0.18;
    localMinY = Math.min(localMinY, _v1.y - footJointRadius);

    const mFoot = _m1.copy(mKnee).multiply(_m2.makeTranslation(0, -lowerLegLen, 0)).multiply(getRotM(pose, side + 'Foot', _m5));
    
    const footDim = dimensions.footDim;
    const ovalOffset = [0, 0, footDim[2]];
    const posFront = [0, 0.05, 0];
    const posBack = [0, -0.05, 0];

    [posFront, posBack].forEach(sideOffset => {
      _v1.set(
        ovalOffset[0] + sideOffset[0],
        ovalOffset[1] + sideOffset[1],
        ovalOffset[2] + sideOffset[2]
      ).applyMatrix4(mFoot);

      _v3.setFromMatrixPosition(mFoot);
      
      _v2.set(1, 0, 0).applyMatrix4(mFoot).sub(_v3).normalize();
      const basisX = _v2.x, basisXy = _v2.y, basisXz = _v2.z;
      
      _v2.set(0, 1, 0).applyMatrix4(mFoot).sub(_v3).normalize();
      const basisY = _v2.x, basisYy = _v2.y, basisYz = _v2.z;
      
      _v2.set(0, 0, 1).applyMatrix4(mFoot).sub(_v3).normalize();
      const basisZ = _v2.x, basisZy = _v2.y, basisZz = _v2.z;
      
      const yExtent = Math.sqrt(
         Math.pow(basisXy * footDim[0], 2) +
         Math.pow(basisYy * footDim[1], 2) +
         Math.pow(basisZy * footDim[2], 2)
      );

      localMinY = Math.min(localMinY, _v1.y - yExtent);
    });
  });

  // torso is child of pelvis at position [0, pelvicHeight, 0]
  const mTorso = _m4.copy(mPelvis).multiply(_m1.makeTranslation(0, dimensions.pelvicHeight, 0)).multiply(getRotM(pose, 'torso', _m5));
  _v1.set(0, stomachHeight, 0).applyMatrix4(mTorso);
  localMinY = Math.min(localMinY, _v1.y);

  const mStomach = _m1.copy(mTorso).multiply(_m2.makeTranslation(0, stomachHeight, 0)).multiply(getRotM(pose, 'stomach', _m5));
  _v1.set(0, chestOffset, 0).applyMatrix4(mStomach);
  localMinY = Math.min(localMinY, _v1.y - 0.2);

  const mChest = _m2.copy(mStomach).multiply(_m1.makeTranslation(0, chestOffset, 0)).multiply(getRotM(pose, 'chest', _m5));
  _v1.set(0, (torsoHeight - stomachHeight - chestOffset), 0).applyMatrix4(mChest);
  localMinY = Math.min(localMinY, _v1.y - 0.2);

  mNeckRoot.copy(mChest).multiply(_m1.makeTranslation(0, (torsoHeight - stomachHeight - chestOffset), 0));
  const mNeck = _m6.copy(mNeckRoot).multiply(getRotM(pose, 'neck', _m2));
  _v1.set(0, neckLen, 0).applyMatrix4(mNeck);
  localMinY = Math.min(localMinY, _v1.y - 0.2); 

  // Upper Body Arms
  ['left', 'right'].forEach(side => {
    const dir = side === 'left' ? 1 : -1;
    
    const mClavicle = _m4.copy(mNeckRoot).multiply(_m1.makeTranslation(dir * (shoulderWidth / 4), 0.1, 0)).multiply(getRotM(pose, side + 'Clavicle', _m2));
    localMinY = Math.min(localMinY, _v1.setFromMatrixPosition(mClavicle).y);

    const mShoulder = _m5.copy(mClavicle).multiply(_m1.makeTranslation(dir * (shoulderWidth * 3/4), -0.1, 0)).multiply(getRotM(pose, side + 'Shoulder', _m2));
    localMinY = Math.min(localMinY, _v1.setFromMatrixPosition(mShoulder).y - 0.2);

    const mElbow = _m1.copy(mShoulder).multiply(_m2.makeTranslation(0, -upperArmLen, 0)).multiply(getRotM(pose, side + 'Elbow', _m4));
    localMinY = Math.min(localMinY, _v1.setFromMatrixPosition(mElbow).y - 0.2);

    const mPalm = _m2.copy(mElbow).multiply(_m4.makeTranslation(0, -lowerArmLen, 0)).multiply(getRotM(pose, side + 'Palm', _m5));
    
    _v1.set(0, -palmDim[1], 0).applyMatrix4(mPalm);
    _v3.setFromMatrixPosition(mPalm);
    
    _v2.set(0, 1, 0).applyMatrix4(mPalm).sub(_v3).normalize();
    const basisYP = _v2.y;
    _v2.set(1, 0, 0).applyMatrix4(mPalm).sub(_v3).normalize();
    const basisXP = _v2.y;
    _v2.set(0, 0, 1).applyMatrix4(mPalm).sub(_v3).normalize();
    const basisZP = _v2.y;
    
    const palmYExtent = Math.sqrt(
       Math.pow(basisXP * palmDim[0], 2) +
       Math.pow(basisYP * palmDim[1], 2) +
       Math.pow(basisZP * palmDim[2], 2)
    );
    localMinY = Math.min(localMinY, _v1.y - palmYExtent);
  });

  const mHead = _m1.copy(mNeck).multiply(_m2.makeTranslation(0, neckLen, 0)).multiply(getRotM(pose, 'head', _m4));
  _v1.set(0, headDim[1], 0).applyMatrix4(mHead);
  
  _v3.setFromMatrixPosition(mHead);
  _v2.set(1, 0, 0).applyMatrix4(mHead).sub(_v3).normalize();
  const basisXH = _v2.y;
  _v2.set(0, 1, 0).applyMatrix4(mHead).sub(_v3).normalize();
  const basisYH = _v2.y;
  _v2.set(0, 0, 1).applyMatrix4(mHead).sub(_v3).normalize();
  const basisZH = _v2.y;
  
  const headYExtent = Math.sqrt(
     Math.pow(basisXH * headDim[0], 2) +
     Math.pow(basisYH * headDim[1], 2) +
     Math.pow(basisZH * headDim[2], 2)
  );

  localMinY = Math.min(localMinY, _v1.y - headYExtent);

  // torsoBase (the pelvis bone's offset from the mannequin's own root, see the
  // "pelvis" BoneGroup in PrimitiveMannequin.tsx) sits inside the orientation-rotated
  // frame but is not itself affected by the pelvis's own local tilt, so it's rotated
  // by orientation alone here rather than folded into mPelvis above.
  const torsoBaseWorldY = _vTorsoBase.set(0, torsoBaseY, 0).applyMatrix4(mOrient).y;

  return 0.05 - (torsoBaseWorldY + localMinY) * 0.5;
};

export const defaultPose: Pose = {
  ...INITIAL_POSE,
  elevation: [0, calculateGroundedElevation(INITIAL_POSE), 0]
};

// Joints owned by the compound thrust/lateral/vertical torso movement below —
// randomizing them independently would fight the hip/knee/foot compensation
// and leave the mannequin floating or clipping through the ground.
const TORSO_COMPOUND_JOINTS = new Set([
  'pelvis', 'torso', 'stomach', 'chest', 'neck',
  'leftHip', 'rightHip', 'leftKnee', 'rightKnee', 'leftFoot', 'rightFoot'
]);

const HIP_HEIGHT_WORLD = 7.77 * 0.5;
const RAD2DEG = 180 / Math.PI;
const MAX_THRUST = 1.44;
const MAX_PELVIC_TILT = 30;
const MAX_SQUAT_A = 180;
const LEG_RATIO = 3.44 / 4.22;

// Mirrors the Thrust/Lateral/Vertical sliders in the Hip sidebar (Sidebar.tsx),
// applied in the same order (thrust, then vertical, then lateral) so a randomized
// pose stays grounded the same way manual slider use does.
const applyTorsoCompoundMovement = (pose: any, thrustPct: number, verticalPct: number, lateralPct: number) => {
  const leftHip = (pose.leftHip || [0, 0, 0]) as [number, number, number];
  const rightHip = (pose.rightHip || [0, 0, 0]) as [number, number, number];
  const leftKnee = (pose.leftKnee || [0, 0, 0]) as [number, number, number];
  const rightKnee = (pose.rightKnee || [0, 0, 0]) as [number, number, number];
  const leftFoot = (pose.leftFoot || [0, 0, 0]) as [number, number, number];
  const rightFoot = (pose.rightFoot || [0, 0, 0]) as [number, number, number];
  const stomach = (pose.stomach || [0, 0, 0]) as [number, number, number];
  const chest = (pose.chest || [0, 0, 0]) as [number, number, number];
  const neck = (pose.neck || [0, 0, 0]) as [number, number, number];
  const torso = (pose.torso || [0, 0, 0]) as [number, number, number];
  const elevation = (pose.elevation || [0, 0, 0]) as [number, number, number];

  // Thrust: translate elevation[2], compensate hip X (extension) and stomach X (backward lean)
  const dZ = (thrustPct / 180) * MAX_THRUST;
  const hipXThrustComp = Math.atan2(dZ, HIP_HEIGHT_WORLD) * RAD2DEG;
  const stomachXComp = -Math.atan2(dZ, HIP_HEIGHT_WORLD * 0.4) * RAD2DEG * 0.6;
  pose.elevation = [elevation[0] || 0, elevation[1] || 0, dZ];
  pose.stomach = [stomachXComp, stomach[1], stomach[2]];

  // Vertical: squat depth, IK-compensated knee bend so feet stay planted
  const a = (verticalPct / 100) * MAX_SQUAT_A;
  const aRad = a * (Math.PI / 180);
  const kneeBeta = a + Math.asin(Math.min(LEG_RATIO * Math.sin(aRad), 1)) * RAD2DEG;
  pose.leftHip = [-a, leftHip[1], leftHip[2]];
  pose.rightHip = [-a, rightHip[1], rightHip[2]];
  pose.leftKnee = [kneeBeta, leftKnee[1], leftKnee[2]];
  pose.rightKnee = [kneeBeta, rightKnee[1], rightKnee[2]];

  // Lateral: pelvic tilt, with counter-rotating spine and knee/foot IK on the dropped side
  const tilt = (lateralPct / 100) * MAX_PELVIC_TILT;
  const absTilt = Math.abs(tilt);
  const tiltRad = tilt * (Math.PI / 180);
  const drop = 0.95 * Math.abs(Math.sin(tiltRad));
  const sinA = Math.min(drop / (4.22 + 3.44), 1);
  const aDeg = Math.asin(sinA) * RAD2DEG;
  const kneeBend = aDeg + Math.asin(Math.min(LEG_RATIO * sinA, 1)) * RAD2DEG + absTilt * 0.5;
  pose.pelvis = [0, 0, tilt];
  pose.leftHip = [pose.leftHip[0], leftHip[1], -tilt];
  pose.rightHip = [pose.rightHip[0], rightHip[1], -tilt];
  pose.leftKnee = [tilt < 0 ? kneeBend : pose.leftKnee[0], leftKnee[1], leftKnee[2]];
  pose.rightKnee = [tilt > 0 ? kneeBend : pose.rightKnee[0], rightKnee[1], rightKnee[2]];
  pose.leftFoot = [tilt * 0.7, leftFoot[1] ?? 0, tilt * 0.7];
  pose.rightFoot = [-tilt * 0.7, rightFoot[1] ?? 0, -tilt * 0.7];
  pose.torso = [torso[0], torso[1], -tilt * 0.5];
  pose.stomach = [pose.stomach[0], pose.stomach[1], -tilt * 0.3];
  pose.chest = [chest[0], chest[1], -tilt * 0.15];
  pose.neck = [neck[0], neck[1], -tilt * 0.05];
};

export const generateRandomPose = (sourcePose: Pose, isMirroring: boolean = false, randomness: number = 100): Pose => {
  const pose: any = { ...sourcePose };
  const joints = Object.keys(INITIAL_POSE);
  const processed = new Set<string>();

  joints.forEach(joint => {
    if (processed.has(joint)) return;
    if (joint === 'elevation' || joint === 'orientation') return;
    if (TORSO_COMPOUND_JOINTS.has(joint)) return;

    const limits = JOINT_CONSTRAINTS[joint];
    if (limits) {
      const cur = (sourcePose[joint] || [0, 0, 0]) as [number, number, number];
      
      const genVal = (idx: number, axis: 'x' | 'y' | 'z') => {
        const range = limits[axis][1] - limits[axis][0];
        const maxDelta = range * (randomness / 100);
        const val = cur[idx] + (Math.random() * 2 - 1) * maxDelta;
        return clamp(val, limits[axis][0], limits[axis][1]);
      };

      const rx = genVal(0, 'x');
      const ry = genVal(1, 'y');
      const rz = genVal(2, 'z');
      
      pose[joint] = [rx, ry, rz];
      processed.add(joint);

      if (isMirroring && MIRROR_MAP[joint]) {
        const mirrorJoint = MIRROR_MAP[joint];
        const mLimits = JOINT_CONSTRAINTS[mirrorJoint];
        if (mLimits) {
          pose[mirrorJoint] = [
            clamp(rx, mLimits.x[0], mLimits.x[1]),
            clamp(-ry, mLimits.y[0], mLimits.y[1]),
            clamp(-rz, mLimits.z[0], mLimits.z[1]),
          ];
          processed.add(mirrorJoint);
        }
      }
    }
  });

  // Compound torso movement first, so hip/knee/foot compensation is computed
  // consistently — same as dragging the Thrust/Vertical/Lateral sliders by hand.
  const scale = randomness / 100;
  const thrustPct = (Math.random() * 2 - 1) * 180 * scale;
  const verticalPct = Math.random() * 100 * scale;
  const lateralPct = (Math.random() * 2 - 1) * 100 * scale;
  applyTorsoCompoundMovement(pose, thrustPct, verticalPct, lateralPct);

  // Then layer independent jitter onto the torso joint itself on top of the
  // compound-set value — torso isn't part of the foot-planting IK chain, so
  // this is safe and is what actually varies the Torso panel's own sliders.
  const torsoLimits = JOINT_CONSTRAINTS.torso;
  if (torsoLimits) {
    const torsoCur = pose.torso as [number, number, number];
    const jitter = (idx: number, axis: 'x' | 'y' | 'z') => {
      const range = torsoLimits[axis][1] - torsoLimits[axis][0];
      const maxDelta = range * scale;
      const val = torsoCur[idx] + (Math.random() * 2 - 1) * maxDelta;
      return clamp(val, torsoLimits[axis][0], torsoLimits[axis][1]);
    };
    pose.torso = [jitter(0, 'x'), jitter(1, 'y'), jitter(2, 'z')];
  }

  // Ensure random pose is grounded
  const targetElevation = calculateGroundedElevation(pose as Pose);
  pose.elevation = [pose.elevation?.[0] || 0, targetElevation, pose.elevation?.[2] || 0];

  return pose as Pose;
};

export const MIRROR_MAP: Record<string, string> = {
  leftClavicle: 'rightClavicle',
  rightClavicle: 'leftClavicle',
  leftShoulder: 'rightShoulder',
  rightShoulder: 'leftShoulder',
  leftElbow: 'rightElbow',
  rightElbow: 'leftElbow',
  leftPalm: 'rightPalm',
  rightPalm: 'leftPalm',
  leftHip: 'rightHip',
  rightHip: 'leftHip',
  leftKnee: 'rightKnee',
  rightKnee: 'leftKnee',
  leftFoot: 'rightFoot',
  rightFoot: 'leftFoot',
};

export const JOINT_PARENT_MAP: Record<string, string> = {
  head: 'neck',
  neck: 'chest',
  chest: 'stomach',
  stomach: 'torso',
  // left arm
  leftPalm: 'leftElbow',
  leftElbow: 'leftShoulder',
  leftShoulder: 'leftClavicle',
  // right arm
  rightPalm: 'rightElbow',
  rightElbow: 'rightShoulder',
  rightShoulder: 'rightClavicle',
  // left leg
  leftFoot: 'leftKnee',
  leftKnee: 'leftHip',
  // right leg
  rightFoot: 'rightKnee',
  rightKnee: 'rightHip',
};


export const JOINT_TO_SECTION: Record<string, string> = {
  leftClavicle: 'joints-shrug',
  rightClavicle: 'joints-shrug',
  leftShoulder: 'joints-shoulder',
  rightShoulder: 'joints-shoulder',
  leftElbow: 'joints-elbow',
  rightElbow: 'joints-elbow',
  leftHip: 'joints-hip',
  rightHip: 'joints-hip',
  leftPalm: 'joints-palm',
  rightPalm: 'joints-palm',
  leftKnee: 'joints-knee',
  rightKnee: 'joints-knee',
  leftFoot: 'joints-foot',
  rightFoot: 'joints-foot',
  head: 'core-head',
  neck: 'core-neck',
  chest: 'core-chest',
  stomach: 'core-stomach',
  torso: 'core-torso'
};

/**
 * Global utility to snap a time value to the active grid (music beats or metronome).
 */
export const snapTimeToGrid = (time: number, state: { hasMusicLoaded: boolean, beats: number[], metronomeInterval: number, beatSubdivision?: number, duration?: number }): number => {
  if (state.hasMusicLoaded && state.beats.length > 0) {
    const ticks: number[] = [];
    const beats = state.beats;
    const subdivision = state.beatSubdivision || 1;
    const duration = state.duration || 10;
    
    if (beats[0] > 0.05) ticks.push(0);
    beats.forEach((b, i) => {
      if (b <= duration) {
        ticks.push(b);
        if (subdivision > 1 && i < beats.length - 1) {
          const nextB = beats[i + 1];
          for (let s = 1; s < subdivision; s++) {
            const subT = b + (nextB - b) * (s / subdivision);
            if (subT <= duration) ticks.push(subT);
          }
        }
      }
    });
    
    if (ticks.length === 0) return time;
    return ticks.reduce((prev, curr) => Math.abs(curr - time) < Math.abs(prev - time) ? curr : prev);
  } else if (state.metronomeInterval > 0) {
    // Standard grid: multiples of metronomeInterval
    return Math.round(time / state.metronomeInterval) * state.metronomeInterval;
  }
  return time;
};





const pushHistory = (state: DanceState, desc: string) => ({
  actionDescription: desc,
  pose: JSON.parse(JSON.stringify(state.currentPose)), // Deep copy pose
  keyframes: [...state.keyframes],
  duration: state.duration,
  frameMarkers: { ...state.frameMarkers }
});

async function handleMusicAutoLoad(musicData: any, set: any, get: any) {
  if (!musicData) return;
  
  if (musicData.sourceUrl) {
    set({ isAnalyzing: true });
    get().confirmAction({
      title: i18n.t('music.loading_sequence_title', { defaultValue: 'Loading Sequence' }),
      message: i18n.t('music.loading_sequence_message', { defaultValue: 'Downloading and validating soundtrack...' }),
      isLoading: true,
      isNonDismissible: true,
      onConfirm: () => {},
    });
    try {
      const audioFile = await get().downloadAndValidateMusic(musicData.sourceUrl, musicData.hash, musicData.fileName);
      await get().loadMusic(audioFile, false, 'url', musicData.sourceUrl);
      get().closeConfirm();
    } catch (err: any) {
      console.error('Failed to auto-load imported sequence music:', err);
      const errorMsg = getFriendlyErrorMessage(err, i18n.t);
      set({ isAnalyzing: false });
      get().closeConfirm();

      const state = get();
      const seq: SavedSequence = {
        id: state.loadedSequence?.id || '',
        name: state.loadedSequence?.name || 'Imported Sequence',
        folderId: null,
        duration: state.duration,
        keyframes: state.keyframes,
        frameMarkers: state.frameMarkers,
        audioFileName: musicData.fileName,
        audioHash: musicData.hash,
        audioSourceUrl: musicData.sourceUrl,
        metronomeInterval: state.metronomeInterval,
        beatSubdivision: state.beatSubdivision,
        dateCreated: Date.now(),
        dateModified: Date.now(),
      };
      get().openSoundtrackModalForSequence({ sequence: seq, initialUrl: musicData.sourceUrl, initialError: errorMsg });
    }
  } else if (musicData.hash) {
    // No URL, only hash — was a local file. Open SoundtrackModal with local-file context.
    const state = get();
    const seq: SavedSequence = {
      id: state.loadedSequence?.id || '',
      name: state.loadedSequence?.name || 'Imported Sequence',
      folderId: null,
      duration: state.duration,
      keyframes: state.keyframes,
      frameMarkers: state.frameMarkers,
      audioFileName: musicData.fileName,
      audioHash: musicData.hash,
      audioSourceUrl: null,
      metronomeInterval: state.metronomeInterval,
      beatSubdivision: state.beatSubdivision,
      dateCreated: Date.now(),
      dateModified: Date.now(),
    };
    get().openSoundtrackModalForSequence({ sequence: seq, isLocalFile: true });
  }
}

let zoomTrackTimeout: any = null;

export const useDanceStore = create<DanceState>((set, get) => ({
  // Global Stage settings
  activeModule: 'PoseDesigner',
  setActiveModule: (module) => {
    trackEvent('editor_tab_switched', { tab_name: module });
    set({ activeModule: module });
  },

  actingSource: { type: 'timeline', time: 0 },
  setActingSource: (source) => set({ actingSource: source }),
  hasUnsavedChanges: false,
  hasUnsavedSequenceChanges: false,
  loadedSequence: null,
  setHasUnsavedSequenceChanges: (v) => set({ hasUnsavedSequenceChanges: v }),
  setLoadedSequence: (id, name, userId, collaborators) => set({ loadedSequence: { id, name, userId, collaborators }, hasUnsavedSequenceChanges: false }),
  setRotation: (deg: number) => set((state: DanceState) => ({
    hasUnsavedChanges: true,
    currentPose: {
      ...state.currentPose,
      orientation: [state.currentPose.orientation?.[0] || 0, deg, state.currentPose.orientation?.[2] || 0]
    }
  })),
  resetOrientation: () => set((state: DanceState) => ({
    hasUnsavedChanges: true,
    currentPose: {
      ...state.currentPose,
      orientation: [0, 0, 0]
    }
  })),

  // Current Pose State
  currentPose: { ...defaultPose },
  groundedElevation: calculateGroundedElevation(defaultPose),

  // History State
  history: [],
  redoStack: [],

  undo: () => set((state: DanceState) => {
    if (state.history.length === 0) return state;
    const prevSnap = state.history[state.history.length - 1];
    setTimeout(() => get().setToastMessage(`Undid: ${prevSnap.actionDescription}`), 0);
    trackEditorEvent('undo_used');
    return {
      currentPose: prevSnap.pose,
      groundedElevation: calculateGroundedElevation(prevSnap.pose),
      keyframes: prevSnap.keyframes,
      duration: prevSnap.duration,
      frameMarkers: prevSnap.frameMarkers,
      history: state.history.slice(0, -1),
      redoStack: [...state.redoStack, pushHistory(state, prevSnap.actionDescription)]
    };
  }),

  redo: () => set((state: DanceState) => {
    if (state.redoStack.length === 0) return state;
    const nextSnap = state.redoStack[state.redoStack.length - 1];
    setTimeout(() => get().setToastMessage(`Redid: ${nextSnap.actionDescription}`), 0);
    trackEditorEvent('redo_used');
    return {
      currentPose: nextSnap.pose,
      groundedElevation: calculateGroundedElevation(nextSnap.pose),
      keyframes: nextSnap.keyframes,
      duration: nextSnap.duration,
      frameMarkers: nextSnap.frameMarkers,
      redoStack: state.redoStack.slice(0, -1),
      history: [...state.history, pushHistory(state, nextSnap.actionDescription)]
    };
  }),

  commitHistory: (desc: any = 'Slider Edit') => set((state: DanceState) => {
    const actionDesc = typeof desc === 'string' ? desc : 'Slider Edit';
    const jointName = state.selectedJoint;
    const rotation = jointName && state.currentPose[jointName] ? state.currentPose[jointName] : null;

    return {
      posingStartJoint: jointName || null,
      posingStartRotation: rotation ? [...rotation] as [number, number, number] : null,
      posingStartActionType: actionDesc,
      history: [...state.history.slice(-49), pushHistory(state, actionDesc)],
      redoStack: [] // Clear redo stack on new action
    };
  }),


  setJointRotation: (joint: string, x: number, y: number, z: number) => set((state: DanceState) => {
    const applyToJoint = (j: string, rx: number, ry: number, rz: number, pose: Pose) => {
      const limits = JOINT_CONSTRAINTS[j];
      if (limits) {
        rx = clamp(rx, limits.x[0], limits.x[1]);
        let min = limits.y[0], max = limits.y[1];
        if (j === 'elevation') {
          min = calculateGroundedElevation(pose);
          max = Infinity;
        }
        ry = clamp(ry, min, max);
        rz = clamp(rz, limits.z[0], limits.z[1]);
      }
      pose[j] = [rx, ry, rz];
    };

    const newPose = { ...state.currentPose };
    applyToJoint(joint, x, y, z, newPose);

    if (state.isMirroring && MIRROR_MAP[joint]) {
      const mirrorJoint = MIRROR_MAP[joint];
      applyToJoint(mirrorJoint, x, -y, -z, newPose);
    }

    const groundElev = calculateGroundedElevation(newPose);
    return { 
      currentPose: newPose, 
      groundedElevation: groundElev,
      hasUnsavedChanges: true 
    };
  }),

  updateJointRotationRelative: (joint: string, axis: 'x' | 'y' | 'z', deltaDeg: number) => set((state: DanceState) => {
    const BASE_AXES: Record<string, THREE.Vector3> = {
      x: new THREE.Vector3(1, 0, 0),
      y: new THREE.Vector3(0, 1, 0),
      z: new THREE.Vector3(0, 0, 1),
    };
    const applyRelative = (j: string, ax: 'x' | 'y' | 'z', dDeg: number, pose: Pose) => {
      const current = (pose[j] || [0, 0, 0]) as [number, number, number];
      const order = getJointRotationOrder(j) as THREE.EulerOrder;
      const a0 = order[0].toLowerCase() as 'x' | 'y' | 'z';
      const a1 = order[1].toLowerCase() as 'x' | 'y' | 'z';
      const a2 = order[2].toLowerCase() as 'x' | 'y' | 'z';

      const angles = {
        x: THREE.MathUtils.degToRad(current[0]),
        y: THREE.MathUtils.degToRad(current[1]),
        z: THREE.MathUtils.degToRad(current[2]),
      };

      // Per-axis quaternions for the first two Euler slots (to find gimbal axes)
      const Q0 = new THREE.Quaternion().setFromAxisAngle(BASE_AXES[a0], angles[a0]);
      const Q1 = new THREE.Quaternion().setFromAxisAngle(BASE_AXES[a1], angles[a1]);

      // Actual rotation axis in parent space for the pressed Euler slot —
      // matches the axis the Option A gimbal ring visually represents
      let actualAxis: THREE.Vector3;
      if (ax === a0) {
        actualAxis = BASE_AXES[a0].clone();
      } else if (ax === a1) {
        actualAxis = BASE_AXES[a1].clone().applyQuaternion(Q0);
      } else {
        actualAxis = BASE_AXES[a2].clone().applyQuaternion(new THREE.Quaternion().copy(Q0).multiply(Q1));
      }

      const currentEuler = new THREE.Euler(angles.x, angles.y, angles.z, order);
      const currentQ = new THREE.Quaternion().setFromEuler(currentEuler);
      const deltaQ = new THREE.Quaternion().setFromAxisAngle(actualAxis, THREE.MathUtils.degToRad(dDeg));
      currentQ.premultiply(deltaQ);

      const newEuler = new THREE.Euler().setFromQuaternion(currentQ, order);
      const newAngles: [number, number, number] = [
        THREE.MathUtils.radToDeg(newEuler.x),
        THREE.MathUtils.radToDeg(newEuler.y),
        THREE.MathUtils.radToDeg(newEuler.z),
      ];

      const limits = JOINT_CONSTRAINTS[j];
      if (limits) {
        newAngles[0] = clamp(newAngles[0], limits.x[0], limits.x[1]);
        newAngles[1] = clamp(newAngles[1], limits.y[0], limits.y[1]);
        newAngles[2] = clamp(newAngles[2], limits.z[0], limits.z[1]);
      }
      pose[j] = newAngles;
    };

    const newPose = { ...state.currentPose };
    applyRelative(joint, axis, deltaDeg, newPose);

    if (state.isMirroring && MIRROR_MAP[joint]) {
      const mirrorJoint = MIRROR_MAP[joint];
      // X (Pitch) is symmetric, Y (Twist) and Z (Roll/Abduction) are inverted for characters
      const mDelta = (axis === 'x') ? deltaDeg : -deltaDeg;
      applyRelative(mirrorJoint, axis, mDelta, newPose);
    }

    const groundElev = calculateGroundedElevation(newPose);
    return { 
      currentPose: newPose, 
      groundedElevation: groundElev,
      hasUnsavedChanges: true 
    };
  }),

  updateGlobalInteraction: (params: TorsoInteractionParams) => set((state: DanceState) => {
    const p = { ...state.currentPose };
    const orient = [...(p.orientation || [0, 0, 0])] as [number, number, number];
    const elev = [...(p.elevation || [0, 0, 0])] as [number, number, number];

    if (params.dx !== undefined) elev[0] += params.dx;
    if (params.dy !== undefined) elev[1] += params.dy;
    if (params.dz !== undefined) elev[2] += params.dz;

    if (params.drx !== undefined) orient[0] += params.drx;
    if (params.dry !== undefined) orient[1] += params.dry;
    if (params.drz !== undefined) orient[2] += params.drz;

    const groundElev = state.groundedElevation; // Use existing since global interaction doesn't change joint angles
    elev[1] = Math.max(elev[1], groundElev);

    p.elevation = elev;
    p.orientation = orient;

    return { 
      currentPose: p, 
      groundedElevation: groundElev,
      hasUnsavedChanges: true 
    };
  }),

  // Rotates the pose around the torso's current world position (instead of the feet,
  // which is what updateGlobalInteraction/orientation does).
  //
  // elevation is a single world-space translation applied outside rotation (see
  // MannequinWrapper in Stage3D.tsx), so any local point p has:
  //   world = R(orientation) * p + elevation
  // With base = (0, -0.05, 0) + torsoLocal (the torso's fixed local offset), solving
  // for the elevation that keeps worldTorso fixed after orientation changes is a
  // plain vector subtraction — no matrix inversion, no gimbal-style singularity.
  updateTorsoRotation: (deltaPitchDeg: number, deltaRollDeg: number) => set((state: DanceState) => {
    const p = { ...state.currentPose };
    const orient = [...(p.orientation || [0, 0, 0])] as [number, number, number];
    const elev = [...(p.elevation || [0, 0, 0])] as [number, number, number];

    const order = 'XYZ';
    const mPelvis = getRotM(p, 'pelvis', _tm1);
    // The pelvis bone itself is offset from the mannequin's own root by
    // dimensions.torsoBase (see the "pelvis" BoneGroup in PrimitiveMannequin.tsx) —
    // this dwarfs pelvicHeight and was missing from earlier attempts at this pivot,
    // which put the "torso" point close to the model root/feet instead. Same
    // scaled-local-to-world pattern as addFootMarker's torsoBaseWorldY.
    const torsoBaseWorldY = MANNEQUIN_DIMENSIONS.torsoBase[1] * 0.5;
    const torsoLocal = _tv1.set(0, MANNEQUIN_DIMENSIONS.pelvicHeight, 0).applyMatrix4(mPelvis).multiplyScalar(0.5);
    torsoLocal.y += torsoBaseWorldY;
    const base = torsoLocal.clone().add(_tv3.set(0, -0.05, 0));

    const eOld = new THREE.Euler(
      THREE.MathUtils.degToRad(orient[0]),
      THREE.MathUtils.degToRad(orient[1]),
      THREE.MathUtils.degToRad(orient[2]),
      order
    );
    const torsoWorld = base.clone().applyEuler(eOld).add(_tv2.set(elev[0], elev[1], elev[2]));

    orient[0] += deltaPitchDeg;
    orient[2] += deltaRollDeg;

    const eNew = new THREE.Euler(
      THREE.MathUtils.degToRad(orient[0]),
      THREE.MathUtils.degToRad(orient[1]),
      THREE.MathUtils.degToRad(orient[2]),
      order
    );
    const rotatedBase = base.clone().applyEuler(eNew);
    const newElev = torsoWorld.clone().sub(rotatedBase);
    elev[0] = newElev.x;
    elev[1] = newElev.y;
    elev[2] = newElev.z;

    // No ground/ceiling clamp here: once the body is pivoted around the torso,
    // "elevation" no longer means "height above floor for a standing pose", so
    // the standing-pose bounds don't carry over. The mannequin is free to float
    // above or below where the floor used to be.
    // groundedElevation still needs recomputing live though (it drives the floor
    // indicator/grounding button) — it's now orientation-aware, so as the body
    // rotates, whichever extremity is currently lowest (e.g. the head once
    // inverted) is what it tracks, not always the feet.
    p.orientation = orient;
    const groundElev = calculateGroundedElevation(p);

    p.elevation = elev;

    return {
      currentPose: p,
      groundedElevation: groundElev,
      hasUnsavedChanges: true
    };
  }),

  resetPose: () => {
    trackEditorEvent('pose_reset');
    const pose = { ...defaultPose };
    set({ currentPose: pose, groundedElevation: calculateGroundedElevation(pose), history: [], hasUnsavedChanges: true });
  },

  setPoseNotes: (notes: string) => set((state: DanceState) => ({
    currentPose: {
      ...state.currentPose,
      notes
    },
    hasUnsavedChanges: true
  })),

  // Timeline & Animation State
  timelineZoom: 1,
  setTimelineZoom: (z: number) => {
    const val = Math.max(0.4, Math.min(1.3, z));
    set({ timelineZoom: val });
    if (zoomTrackTimeout) clearTimeout(zoomTrackTimeout);
    zoomTrackTimeout = setTimeout(() => {
      trackTimelineEvent('zoom_action', { zoom: val });
    }, 500);
  },
  isTimelineCompact: false,
  setIsTimelineCompact: (compact: boolean) => {
    trackTimelineEvent('compact_view_toggled', { enabled: compact });
    set({ isTimelineCompact: compact });
    useStorageStore.getState().updatePreference('compactTimeline', compact);
  },
  randomnessAmount: 10,
  setRandomnessAmount: (val: number) => set({ randomnessAmount: Math.max(0, Math.min(100, val)) }),
  keyframes: [], 
  duration: 10,
  metronomeInterval: 0.5,
  beatSubdivision: 1,
  currentTime: 0,
  isPlaying: false,
  showLabels: false,
  showAxes: true,
  selectedJoint: null,
  isPosing: false,
  posingStartRotation: null,
  posingStartJoint: null,
  posingStartActionType: null,
  isMirroring: true,
  copiedPose: null,
  activeAxis: null,
  minimizedSidebars: { textToPose: true, joints: false, core: false, attitude: false, reset: false, studio: false },
  collapsedSections: {
    'joints-shrug': true,
    'joints-shoulder': true,
    'joints-elbow': true,
    'joints-hip': true,
    'joints-palm': true,
    'joints-knee': true,
    'joints-foot': true,
    'core-head': true,
    'core-neck': true,
    'core-chest': true,
    'core-stomach': true,
    'core-torso': true
  },
  isHoveringJoint: false,
  setIsHoveringJoint: (hovered: boolean) => set({ isHoveringJoint: hovered }),

  // Music synchronization state
  audioFile: null,
  audioFileName: null,
  audioUrl: null,
  audioDuration: null,
  beats: [],
  measures: [],
  bpm: null,
  isAnalyzing: false,
  musicVolume: 0.8,
  waveformData: [],
  hasMusicLoaded: false,
  audioHash: null,
  audioSourceUrl: null,
  frameMarkers: {},
  confirmDialog: null,
  confirmReset: true,
  autoSaveEnabled: false,
  inputDialog: null,
  isSoundtrackModalOpen: false,
  setSoundtrackModalOpen: (open) => set({ isSoundtrackModalOpen: open }),
  soundtrackModalContext: null,
  openSoundtrackModalForSequence: (ctx) => set({ soundtrackModalContext: ctx, isSoundtrackModalOpen: true }),
  clearSoundtrackModalContext: () => set({ soundtrackModalContext: null }),
  pendingMusicSequence: null,
  setPendingMusicSequence: (seq) => set({ pendingMusicSequence: seq }),
  musicLoadingError: null,
  setMusicLoadingError: (error) => set({ musicLoadingError: error }),
  isSettingsOpen: false,
  setIsSettingsOpen: (open) => set({ isSettingsOpen: open }),
  isFeedbackOpen: false,
  setIsFeedbackOpen: (open) => set({ isFeedbackOpen: open }),
  feedbackMessage: '',
  setFeedbackMessage: (msg) => set({ feedbackMessage: msg }),
  feedbackScreenshot: null,
  setFeedbackScreenshot: (screenshot) => set({ feedbackScreenshot: screenshot }),
  toastMessage: null,
  toastType: 'info',
  setToastMessage: (msg, type = 'info') => {
    set({ toastMessage: msg, toastType: type });
    if (msg) {
      setTimeout(() => set((state) => state.toastMessage === msg ? { toastMessage: null } : {}), 3000);
    }
  },
  cameraResetVersion: 0,
  resetCamera: () => {
    trackEditorEvent('camera_reset');
    set((state: DanceState) => ({ cameraResetVersion: state.cameraResetVersion + 1 }));
  },
  showCameraDebug: false,
  setShowCameraDebug: (show) => set({ showCameraDebug: show }),

  selectedFrameTimes: [],
  setSelectedFrameTimes: (times) => set({ selectedFrameTimes: times }),
  copiedFrames: [],
  contextMenu: null,

  librarySelection: [],
  setLibrarySelection: (ids) => set({ librarySelection: ids }),
  toggleLibrarySelection: (id, multi, range, allIds) => set((state: DanceState) => {
    if (range && allIds && state.librarySelection.length > 0) {
      const lastSelected = state.librarySelection[state.librarySelection.length - 1];
      const startIdx = allIds.indexOf(lastSelected);
      const endIdx = allIds.indexOf(id);
      if (startIdx !== -1 && endIdx !== -1) {
        const min = Math.min(startIdx, endIdx);
        const max = Math.max(startIdx, endIdx);
        const rangeIds = allIds.slice(min, max + 1);
        const newSelection = Array.from(new Set([...state.librarySelection, ...rangeIds]));
        return { librarySelection: newSelection };
      }
    }
    
    if (multi) {
      const newSelection = state.librarySelection.includes(id)
        ? state.librarySelection.filter(sid => sid !== id)
        : [...state.librarySelection, id];
      return { librarySelection: newSelection };
    }
    
    return { librarySelection: [id] };
  }),

  footMarkers: [],
  addFootMarker: (dimensions = MANNEQUIN_DIMENSIONS) => set((state: DanceState) => {
    const pose = state.currentPose;
    const groundElev = calculateGroundedElevation(pose, dimensions);
    const currentElev = (pose.elevation || [0,0,0])[1];
    
    // Safety check - button should be disabled if not grounded, but we check here too
    if (Math.abs(currentElev - groundElev) > 0.01) return state;

    trackEditorEvent('marker_placed', { type: 'foot' });

    const nextLabel = (state.footMarkers.length > 0) 
      ? Math.max(...state.footMarkers.map(m => m.label)) + 1 
      : 1;

    // Logic aligned with calculateGroundedElevation but focused on feet
    const torsoBaseY = dimensions.torsoBase[1]; 
    const upperLegLen = dimensions.upperLegLen;
    const lowerLegLen = dimensions.lowerLegLen;
    const hipOffset = dimensions.hipOffset;
    
    const getRotM = (joint: string) => {
      const angles = (pose[joint] as JointRotation) || [0, 0, 0];
      return new THREE.Matrix4().makeRotationFromEuler(
        new THREE.Euler(
          THREE.MathUtils.degToRad(angles[0]),
          THREE.MathUtils.degToRad(angles[1]),
          THREE.MathUtils.degToRad(angles[2]),
          getJointRotationOrder(joint) as any
        )
      );
    };

    const newMarkers = [...state.footMarkers];
    const orientationY = pose.orientation?.[1] || 0;
    const worldCenter = new THREE.Vector3(pose.elevation?.[0] || 0, 0, pose.elevation?.[2] || 0);

    ['left', 'right'].forEach(side => {
      const dir = side === 'left' ? 1 : -1;
      const hipOffsetM = new THREE.Matrix4().setPosition(hipOffset[0] * dir, hipOffset[1], hipOffset[2]);
      const mHip = hipOffsetM.multiply(getRotM(side + 'Hip'));
      
      const kneeOffsetM = new THREE.Matrix4().setPosition(0, -upperLegLen, 0);
      const mKnee = mHip.clone().multiply(kneeOffsetM).multiply(getRotM(side + 'Knee'));
      
      const footOffsetM = new THREE.Matrix4().setPosition(0, -lowerLegLen, 0);
      const mFoot = mKnee.clone().multiply(footOffsetM).multiply(getRotM(side + 'Foot'));
      
      // Calculate foot position in "mannequin space" (relative to torso base)
      // The foot mesh center is offset by dimensions.footDim[2] along the Z axis
      const footPos = new THREE.Vector3(0, 0, dimensions.footDim[2]).applyMatrix4(mFoot);
      
      // Transform to world space
      // 1. Scale down by 0.5 (as in calculateGroundedElevation)
      // 2. Add torso base height (scaled)
      // 3. Apply orientation
      // 4. Add elevation [X, Z]
      
      const torsoBaseWorldY = torsoBaseY * 0.5;
      const localWorldPos = footPos.clone().multiplyScalar(0.5);
      localWorldPos.y += torsoBaseWorldY;
      
      // Rotate by orientation
      localWorldPos.applyAxisAngle(new THREE.Vector3(0, 1, 0), orientationY);
      
      // Add horizontal translation
      const finalPos: [number, number, number] = [
        worldCenter.x + localWorldPos.x,
        0.01, // Flat on ground (with tiny offset to avoid Z-fighting)
        worldCenter.z + localWorldPos.z
      ];

      // Foot rotation:
      // We want the rotation of the foot on the Y axis relative to the world.
      // mFoot has the total rotation.
      const worldFootEuler = new THREE.Euler().setFromRotationMatrix(mFoot);
      const finalYRotation = worldFootEuler.y + orientationY;

      newMarkers.push({
        position: finalPos,
        rotation: finalYRotation,
        label: nextLabel,
        side: side as 'left' | 'right'
      });
    });

    return { footMarkers: newMarkers, hasUnsavedSequenceChanges: true };
  }),

  removeFootMarker: (label: number) => {
    trackEditorEvent('marker_deleted', { type: 'foot', label });
    return set((state: DanceState) => ({
      footMarkers: state.footMarkers.filter(m => m.label !== label),
      selectedFootMarkerLabel: state.selectedFootMarkerLabel === label ? null : state.selectedFootMarkerLabel,
      hasUnsavedSequenceChanges: true
    }));
  },

  snapToFootMarkerLabel: (label: number) => {
    trackEditorEvent('marker_snapped', { type: 'foot', label });
    return set((state: DanceState) => {
    const pair = state.footMarkers.filter(m => m.label === label);
    if (pair.length < 2) return state;

    const left = pair.find(m => m.side === 'left')!;
    const right = pair.find(m => m.side === 'right')!;

    const pL = new THREE.Vector3(...left.position);
    const pR = new THREE.Vector3(...right.position);

    const center = pL.clone().add(pR).multiplyScalar(0.5);
    const vec = pL.clone().sub(pR);
    
    // Desired orientation (Yaw)
    const yaw = Math.atan2(-vec.z, vec.x);

    const newPose = { ...state.currentPose };
    const orient = [...(newPose.orientation || [0,0,0])] as [number, number, number];
    orient[1] = THREE.MathUtils.radToDeg(yaw);

    // Shared leg constants and helpers
    const upperLegLen = 4.0;
    const lowerLegLen = 3.66;
    const hipOffsetX = 0.61;

    const getRotM = (joint: string) => {
      const angles = (newPose[joint] as JointRotation) || [0, 0, 0];
      return new THREE.Matrix4().makeRotationFromEuler(
        new THREE.Euler(
          THREE.MathUtils.degToRad(angles[0]),
          THREE.MathUtils.degToRad(angles[1]),
          THREE.MathUtils.degToRad(angles[2]),
          getJointRotationOrder(joint) as any
        )
      );
    };

    const getLocalFootCenter = (side: 'left' | 'right') => {
      const mHipBase = new THREE.Matrix4().identity();
      const kneeOffsetM = new THREE.Matrix4().setPosition(0, -upperLegLen, 0);
      const mKnee = mHipBase.clone().multiply(kneeOffsetM).multiply(getRotM(side + 'Knee'));
      const footOffsetM = new THREE.Matrix4().setPosition(0, -lowerLegLen, 0);
      const mFoot = mKnee.clone().multiply(footOffsetM).multiply(getRotM(side + 'Foot'));
      return new THREE.Vector3(0, 0, 0.8).applyMatrix4(mFoot).multiplyScalar(0.5);
    };

    // Calculate root offset to prevent forward shift
    const localL = getLocalFootCenter('left');
    const localR = getLocalFootCenter('right');
    const localAvg = localL.clone().add(localR).multiplyScalar(0.5);
    localAvg.x = 0; 
    const worldOffset = localAvg.clone().applyAxisAngle(new THREE.Vector3(0, 1, 0), yaw);
    
    const elev = [...(newPose.elevation || [0,0,0])] as [number, number, number];
    elev[0] = center.x - worldOffset.x;
    elev[2] = center.z - worldOffset.z;

    // Calculate hip Z rotation to match marker distance
    const markerDist = pL.distanceTo(pR) / 0.5;
    const targetX = (markerDist / 2) - hipOffsetX;

    const solveHipZ = (side: 'left' | 'right', tx: number) => {
      const mHipBase = new THREE.Matrix4().identity();
      const kneeOffsetM = new THREE.Matrix4().setPosition(0, -upperLegLen, 0);
      const mKnee = mHipBase.clone().multiply(kneeOffsetM).multiply(getRotM(side + 'Knee'));
      const footOffsetM = new THREE.Matrix4().setPosition(0, -lowerLegLen, 0);
      const mFoot = mKnee.clone().multiply(footOffsetM).multiply(getRotM(side + 'Foot'));
      
      const localFootCenter = new THREE.Vector3(0, 0, 0.8).applyMatrix4(mFoot);
      const r = localFootCenter.x;
      const h = localFootCenter.y;
      
      const hypot = Math.sqrt(r * r + h * h);
      const phi = Math.atan2(-h, r); 
      const cosVal = tx / hypot;
      
      const angle = phi - Math.acos(THREE.MathUtils.clamp(cosVal, -1, 1));
      return THREE.MathUtils.radToDeg(angle);
    };

    const leftZ = solveHipZ('left', targetX);
    const rightZ = solveHipZ('right', -targetX);

    newPose.leftHip = [newPose.leftHip?.[0] || 0, newPose.leftHip?.[1] || 0, leftZ];
    newPose.rightHip = [newPose.rightHip?.[0] || 0, newPose.rightHip?.[1] || 0, rightZ];

    const finalElevationY = calculateGroundedElevation(newPose);
    newPose.elevation = [elev[0], finalElevationY, elev[2]];
    newPose.orientation = orient;

    return {
      currentPose: newPose,
      groundedElevation: finalElevationY,
      hasUnsavedChanges: true,
      history: [...state.history.slice(-49), pushHistory(state, 'Snap to Foot Marker')],
      redoStack: []
    };
    });
  },

  clearFootMarkers: () => set({ footMarkers: [], selectedFootMarkerLabel: null, hasUnsavedSequenceChanges: true }),

  selectedFootMarkerLabel: null,
  setSelectedFootMarkerLabel: (label: number | null) => {
    if (label !== null) {
      trackEditorEvent('marker_selected_3d', { type: 'foot', label });
    }
    set({ selectedFootMarkerLabel: label });
  },

  locationMarkers: [],
  addLocationMarker: () => {
    trackEditorEvent('marker_placed', { type: 'location' });
    return set((state: DanceState) => {
      const nextLabel = (state.locationMarkers.length > 0) 
        ? Math.max(...state.locationMarkers.map(m => m.label)) + 1 
        : 1;

      const pose = state.currentPose;
      const finalPos: [number, number, number] = [
        pose.elevation?.[0] || 0,
        0.01, // Flat on ground
        pose.elevation?.[2] || 0
      ];

      return {
        locationMarkers: [...state.locationMarkers, { position: finalPos, label: nextLabel }],
        hasUnsavedSequenceChanges: true
      };
    });
  },

  addLocationMarkerAtPosition: (position: [number, number, number]) => {
    trackEditorEvent('marker_placed', { type: 'location', source: 'floor_click' });
    return set((state: DanceState) => {
      const nextLabel = (state.locationMarkers.length > 0)
        ? Math.max(...state.locationMarkers.map(m => m.label)) + 1
        : 1;
      const finalPos: [number, number, number] = [position[0], 0.01, position[2]];
      return {
        locationMarkers: [...state.locationMarkers, { position: finalPos, label: nextLabel }],
        hasUnsavedSequenceChanges: true
      };
    });
  },

  removeLocationMarker: (label: number) => {
    trackEditorEvent('marker_deleted', { type: 'location', label });
    return set((state: DanceState) => ({
      locationMarkers: state.locationMarkers.filter(m => m.label !== label),
      selectedLocationMarkerLabel: state.selectedLocationMarkerLabel === label ? null : state.selectedLocationMarkerLabel,
      hasUnsavedSequenceChanges: true
    }));
  },

  snapToLocationMarkerLabel: (label: number) => {
    trackEditorEvent('marker_snapped', { type: 'location', label });
    return set((state: DanceState) => {
      const marker = state.locationMarkers.find(m => m.label === label);
      if (!marker) return state;

      const newPose = { ...state.currentPose };
      const elev = [...(newPose.elevation || [0,0,0])] as [number, number, number];
      elev[0] = marker.position[0];
      elev[2] = marker.position[2];
      newPose.elevation = elev;

      return { 
        currentPose: newPose, 
        hasUnsavedChanges: true,
        history: [...state.history.slice(-49), pushHistory(state, 'Snap to Location Marker')],
        redoStack: []
      };
    });
  },

  clearLocationMarkers: () => set({ locationMarkers: [], selectedLocationMarkerLabel: null, hasUnsavedSequenceChanges: true }),

  clearAllMarkers: () => {
    const { confirmAction, clearFootMarkers, clearLocationMarkers, closeConfirm } = get();
    confirmAction({
      title: 'Delete ALL Markers',
      message: 'Are you sure you want to delete all foot and location markers?',
      onConfirm: () => {
        trackEditorEvent('markers_cleared');
        clearFootMarkers();
        clearLocationMarkers();
        closeConfirm();
      }
    });
  },

  selectedLocationMarkerLabel: null,
  setSelectedLocationMarkerLabel: (label: number | null) => {
    if (label !== null) {
      trackEditorEvent('marker_selected_3d', { type: 'location', label });
    }
    set({ selectedLocationMarkerLabel: label });
  },

  lastCameraState: null,
  setLastCameraState: (cameraState) => set({ lastCameraState: cameraState }),

  setActiveAxis: (axis: 'X' | 'Y' | 'Z' | 'A' | 'S' | 'D' | 'F' | null) => set({ activeAxis: axis }),
  setMinimizedSidebar: (name: string, minimized: boolean) => set((state: DanceState) => {
    const newMinimized = { ...state.minimizedSidebars, [name]: minimized };
    if (name === 'textToPose' && !minimized) {
      newMinimized.joints = true;
    } else if ((name === 'joints' || name === 'reset') && !minimized) {
      newMinimized.textToPose = true;
    }
    return { minimizedSidebars: newMinimized };
  }),
  setSectionCollapsed: (sectionId, collapsed) => set((state: DanceState) => {
    const newCollapsed = { ...state.collapsedSections };
    if (!collapsed) {
      // If expanding this section, collapse all others
      Object.keys(newCollapsed).forEach(key => {
        newCollapsed[key] = true;
      });
    }
    newCollapsed[sectionId] = collapsed;
    return { collapsedSections: newCollapsed };
  }),

  setDuration: (d: number) => {
    trackTimelineEvent('settings_changed', { type: 'duration', value: d });
    return set((state: DanceState) => {
      const newKeyframes = state.keyframes.filter(kf => kf.time <= d);
      const newFrameMarkers: Record<string, number[]> = {};
      Object.entries(state.frameMarkers).forEach(([timeStr, dots]) => {
        if (parseFloat(timeStr) <= d) {
          newFrameMarkers[timeStr] = dots;
        }
      });
      return { duration: d, keyframes: newKeyframes, frameMarkers: newFrameMarkers, currentTime: Math.min(state.currentTime, d), hasUnsavedSequenceChanges: true };
    });
  },

  setMetronomeInterval: (interval: number) => {
    trackTimelineEvent('settings_changed', { type: 'metronome_interval', value: interval });
    return set((state: DanceState) => {
      if (interval <= 0) return { metronomeInterval: interval };
      const newKeyframes = state.keyframes.map(kf => ({
        ...kf,
        time: Math.round(kf.time / interval) * interval
      }));
      const uniqueMap = new Map<string, Keyframe>();
      newKeyframes.forEach(kf => {
        uniqueMap.set(kf.time.toFixed(3), kf);
      });
      const uniqueKeyframes = Array.from(uniqueMap.values()).filter(kf => kf.time <= state.duration);
      uniqueKeyframes.sort((a, b) => a.time - b.time);

      const newFrameMarkers: Record<string, number[]> = {};
      Object.entries(state.frameMarkers).forEach(([timeStr, dots]) => {
        const t = parseFloat(timeStr);
        const snappedT = Math.round(t / interval) * interval;
        if (snappedT <= state.duration) {
          const key = snappedT.toFixed(3);
          const existing = newFrameMarkers[key] || [];
          newFrameMarkers[key] = Array.from(new Set([...existing, ...dots]));
        }
      });

      let newCurrentTime = state.currentTime;
      if (!state.isPlaying) {
        newCurrentTime = Math.round(state.currentTime / interval) * interval;
      }
      return { metronomeInterval: interval, keyframes: uniqueKeyframes, frameMarkers: newFrameMarkers, currentTime: Math.min(newCurrentTime, state.duration) };
    });
  },

  setBeatSubdivision: (val: number) => {
    trackTimelineEvent('settings_changed', { type: 'beat_subdivision', value: val });
    return set((state: DanceState) => {
      const beats = state.beats;
      const duration = state.duration;

      // 1. Generate new ticks for the subdivision
      const newTicks: number[] = [];
      if (beats.length > 0) {
        if (beats[0] > 0.05) newTicks.push(0);
        beats.forEach((b, i) => {
          if (b <= duration) {
            newTicks.push(b);
            if (val > 1 && i < beats.length - 1) {
              const nextB = beats[i + 1];
              for (let s = 1; s < val; s++) {
                const subT = b + (nextB - b) * (s / val);
                if (subT <= duration) newTicks.push(subT);
              }
            }
          }
        });
      }

      if (newTicks.length === 0) {
        return { beatSubdivision: val };
      }

      // Helper function to snap a time value to the closest tick in newTicks
      const snapToClosestTick = (t: number): number => {
        return newTicks.reduce((prev, curr) => Math.abs(curr - t) < Math.abs(prev - t) ? curr : prev);
      };

      // 2. Snap keyframes
      const newKeyframes = state.keyframes.map(kf => ({
        ...kf,
        time: snapToClosestTick(kf.time)
      }));
      const uniqueMap = new Map<string, Keyframe>();
      newKeyframes.forEach(kf => {
        uniqueMap.set(kf.time.toFixed(3), kf);
      });
      const uniqueKeyframes = Array.from(uniqueMap.values()).filter(kf => kf.time <= duration);
      uniqueKeyframes.sort((a, b) => a.time - b.time);

      // 3. Snap frame markers
      const newFrameMarkers: Record<string, number[]> = {};
      Object.entries(state.frameMarkers).forEach(([timeStr, dots]) => {
        const t = parseFloat(timeStr);
        const snappedT = snapToClosestTick(t);
        if (snappedT <= duration) {
          const key = snappedT.toFixed(3);
          const existing = newFrameMarkers[key] || [];
          newFrameMarkers[key] = Array.from(new Set([...existing, ...dots]));
        }
      });

      // 4. Snap current time
      let newCurrentTime = state.currentTime;
      if (!state.isPlaying) {
        newCurrentTime = snapToClosestTick(state.currentTime);
      }

      return {
        beatSubdivision: val,
        keyframes: uniqueKeyframes,
        frameMarkers: newFrameMarkers,
        currentTime: Math.min(newCurrentTime, duration),
        hasUnsavedSequenceChanges: true
      };
    });
  },

  setCurrentTime: (t: number) => set((state: DanceState) => {
    // If we're editing a library pose and have changes, don't load the frame pose (Sticky Workspace)
    if (state.actingSource.type === 'library' && state.hasUnsavedChanges) {
      return { currentTime: t };
    }

    // Otherwise, check if there's a keyframe to load at this time
    const kf = state.keyframes.find(kf => Math.abs(kf.time - t) < 0.05);
    if (kf && kf.pose) {
      return { 
        currentTime: t, 
        currentPose: { ...kf.pose }, 
        groundedElevation: calculateGroundedElevation(kf.pose),
        lastCameraState: kf.cameraState || state.lastCameraState,
        actingSource: { type: 'timeline', time: t },
        history: [], 
        hasUnsavedChanges: false 
      };
    }

    return { 
      currentTime: t, 
      actingSource: (state.actingSource.type === 'timeline' || state.actingSource.type === 'none' || !state.hasUnsavedChanges) 
        ? { type: 'timeline', time: t } 
        : state.actingSource,
      history: [], 
      hasUnsavedChanges: false 
    };
  }),
  setIsPlaying: (playing: boolean) => {
    trackTimelineEvent('playback_action', { is_playing: playing });
    set({ isPlaying: playing });
  },
  setShowLabels: (show: boolean) => {
    trackEditorEvent('labels_toggled', { enabled: show });
    set({ showLabels: show });
    useStorageStore.getState().updatePreference('showJointLabels', show);
  },
  setShowAxes: (show: boolean) => {
    trackEditorEvent('axes_toggled', { enabled: show });
    set({ showAxes: show });
    useStorageStore.getState().updatePreference('showJointAxes', show);
  },
  setSelectedJoint: (joint: string | null) => set((state: DanceState) => {
    if (state.isReadOnly) {
      return { selectedJoint: null };
    }
    const newCollapsed = { ...state.collapsedSections };
    const newMinimized = { ...state.minimizedSidebars };
    
    // Collapse everything
    Object.keys(newCollapsed).forEach(key => {
      newCollapsed[key] = true;
    });

    if (joint) {
      // Expand the relevant section
      const sectionId = JOINT_TO_SECTION[joint];
      if (sectionId) {
        newCollapsed[sectionId] = false;
        
        // Auto-expand the parent sidebar containing this joint
        const sidebarName = sectionId.split('-')[0]; // 'joints' or 'core'
        if (sidebarName in newMinimized) {
          newMinimized[sidebarName] = false;
          if (sidebarName === 'joints') {
            newMinimized.textToPose = true;
          }
        }
      }

      // Also expand the parent joint section
      const parentJoint = JOINT_PARENT_MAP[joint];
      if (parentJoint) {
        const parentSectionId = JOINT_TO_SECTION[parentJoint];
        if (parentSectionId) {
          newCollapsed[parentSectionId] = false;
          
          // Auto-expand the parent sidebar containing this parent joint
          const parentSidebarName = parentSectionId.split('-')[0];
          if (parentSidebarName in newMinimized) {
            newMinimized[parentSidebarName] = false;
            if (parentSidebarName === 'joints') {
              newMinimized.textToPose = true;
            }
          }
        }
      }
    }
    
    return { 
      selectedJoint: joint,
      collapsedSections: newCollapsed,
      minimizedSidebars: newMinimized
    };
  }),
  setIsPosing: (posing: boolean) => set((state: DanceState) => {
    const updates: Partial<DanceState> = { isPosing: posing };

    if (state.isPosing && !posing) {
      // Transition from posing to finished: compute delta and track telemetry
      const joint = state.posingStartJoint || state.selectedJoint;
      const startRot = state.posingStartRotation;
      if (joint && startRot && state.currentPose[joint]) {
        const currentRot = state.currentPose[joint] as JointRotation;
        const changedX = Math.abs(currentRot[0] - startRot[0]) > 0.01;
        const changedY = Math.abs(currentRot[1] - startRot[1]) > 0.01;
        const changedZ = Math.abs(currentRot[2] - startRot[2]) > 0.01;

        const changedAxes: string[] = [];
        if (changedX) changedAxes.push('x');
        if (changedY) changedAxes.push('y');
        if (changedZ) changedAxes.push('z');

        if (changedAxes.length > 0) {
          trackEditorEvent('completed_pose_action', {
            action_type: state.posingStartActionType || 'Slider Edit',
            target_joint: joint,
            is_mirroring: state.isMirroring,
            angle_x: Number(currentRot[0].toFixed(2)),
            angle_y: Number(currentRot[1].toFixed(2)),
            angle_z: Number(currentRot[2].toFixed(2)),
            changed_axes: changedAxes.join(',')
          });
        }
      }
      updates.posingStartJoint = null;
      updates.posingStartRotation = null;
      updates.posingStartActionType = null;
    }

    return updates;
  }),
  setIsMirroring: (mirroring: boolean) => {
    trackEditorEvent('mirror_mode_toggled', { enabled: mirroring });
    set({ isMirroring: mirroring });
    useStorageStore.getState().updatePreference('mirrorMode', mirroring);
  },

  addKeyframe: (cameraState, explicitTime) => set((state: DanceState) => {
    const targetTime = explicitTime !== undefined ? explicitTime : snapTimeToGrid(state.currentTime, state);
    trackTimelineEvent('keyframe_inserted', { time: targetTime });
    const existingIndex = state.keyframes.findIndex(kf => Math.abs(kf.time - targetTime) < 0.05);
    const existingKf = existingIndex >= 0 ? state.keyframes[existingIndex] : null;
    const newKf: Keyframe = { 
      time: targetTime, 
      pose: { ...state.currentPose },
      cameraState: cameraState || state.lastCameraState || undefined,
      note: existingKf?.note
    };
    let newKeyframes;
    if (existingIndex >= 0) {
      newKeyframes = [...state.keyframes];
      newKeyframes[existingIndex] = newKf;
    } else {
      newKeyframes = [...state.keyframes, newKf];
    }
    newKeyframes.sort((a, b) => a.time - b.time);
    return { keyframes: newKeyframes, currentTime: targetTime, actingSource: { type: 'timeline', time: targetTime }, hasUnsavedChanges: false, hasUnsavedSequenceChanges: true, history: [...state.history.slice(-49), pushHistory(state, 'Add Keyframe')], redoStack: [] };
  }),

  removeKeyframe: (time: number) => set((state: DanceState) => {
    const isEditingThisFrame = state.actingSource.type === 'timeline' && Math.abs(state.actingSource.time - time) < 0.05;
    trackTimelineEvent('keyframe_deleted', { time: time });
    return {
      keyframes: state.keyframes.filter(kf => Math.abs(kf.time - time) > 0.05),
      hasUnsavedChanges: isEditingThisFrame ? false : state.hasUnsavedChanges,
      actingSource: state.actingSource.type === 'timeline' ? { type: 'timeline', time } : state.actingSource,
      hasUnsavedSequenceChanges: true,
      history: [...state.history.slice(-49), pushHistory(state, 'Remove Keyframe')],
      redoStack: []
    };
  }),

  setKeyframeNote: (time: number, note: string) => set((state: DanceState) => {
    const targetTime = snapTimeToGrid(time, state);
    const existingIdx = state.keyframes.findIndex(kf => Math.abs(kf.time - targetTime) < 0.05);
    let newKeyframes = [...state.keyframes];
    
    if (existingIdx >= 0) {
      newKeyframes[existingIdx] = {
        ...newKeyframes[existingIdx],
        note
      };
    } else {
      newKeyframes.push({
        time: targetTime,
        note
      });
      newKeyframes.sort((a, b) => a.time - b.time);
    }
    
    return {
      keyframes: newKeyframes,
      hasUnsavedSequenceChanges: true,
      history: [...state.history.slice(-49), pushHistory(state, 'Edit Keyframe Note')],
      redoStack: []
    };
  }),

  setKeyframePoseNotes: (time: number, notes: string) => set((state: DanceState) => {
    const targetTime = snapTimeToGrid(time, state);
    const existingIdx = state.keyframes.findIndex(kf => Math.abs(kf.time - targetTime) < 0.05);
    let newKeyframes = [...state.keyframes];
    let nextCurrentPose = state.currentPose;
    let nextHasUnsavedChanges = state.hasUnsavedChanges;
    
    if (existingIdx >= 0) {
      const kf = newKeyframes[existingIdx];
      if (kf.pose) {
        newKeyframes[existingIdx] = {
          ...kf,
          pose: {
            ...kf.pose,
            notes
          }
        };
        if (state.actingSource.type === 'timeline' && Math.abs(state.actingSource.time - targetTime) < 0.05) {
          nextCurrentPose = {
            ...state.currentPose,
            notes
          };
          nextHasUnsavedChanges = true;
        }
      }
    }
    
    return {
      keyframes: newKeyframes,
      currentPose: nextCurrentPose,
      hasUnsavedChanges: nextHasUnsavedChanges,
      hasUnsavedSequenceChanges: true,
      history: [...state.history.slice(-49), pushHistory(state, 'Edit Keyframe Pose Notes')],
      redoStack: []
    };
  }),

  deleteKeyframeNote: (time: number) => set((state: DanceState) => {
    const targetTime = snapTimeToGrid(time, state);
    const existingIdx = state.keyframes.findIndex(kf => Math.abs(kf.time - targetTime) < 0.05);
    if (existingIdx === -1) return state;
    
    let newKeyframes = [...state.keyframes];
    const kf = newKeyframes[existingIdx];
    
    if (!kf.pose && !kf.cameraState) {
      // If no pose and no cameraState, delete the keyframe entirely
      newKeyframes = newKeyframes.filter((_, idx) => idx !== existingIdx);
    } else {
      // Otherwise just delete the note property
      const updatedKf = { ...kf };
      delete updatedKf.note;
      newKeyframes[existingIdx] = updatedKf;
    }
    
    return {
      keyframes: newKeyframes,
      hasUnsavedSequenceChanges: true,
      history: [...state.history.slice(-49), pushHistory(state, 'Delete Keyframe Note')],
      redoStack: []
    };
  }),

  updateKeyframeTime: (oldTime: number, newTime: number) => set((state: DanceState) => {
    let targetTime = snapTimeToGrid(newTime, state);
    targetTime = Math.max(0, Math.min(state.duration, targetTime));
    trackTimelineEvent('keyframe_moved', { from_time: oldTime, to_time: targetTime });
    const kfToMove = state.keyframes.find(kf => Math.abs(kf.time - oldTime) < 0.05);
    if (!kfToMove) return state;
    let newKeyframes = state.keyframes.filter(kf => Math.abs(kf.time - oldTime) >= 0.05 && Math.abs(kf.time - targetTime) >= 0.05);
    newKeyframes.push({ ...kfToMove, time: targetTime });
    newKeyframes.sort((a, b) => a.time - b.time);
    return { keyframes: newKeyframes, currentTime: targetTime, hasUnsavedSequenceChanges: true, history: [...state.history.slice(-49), pushHistory(state, 'Move Keyframe')], redoStack: [] };
  }),

  copyKeyframe: (fromTime: number, toTime: number) => set((state: DanceState) => {
    const kfToCopy = state.keyframes.find(kf => Math.abs(kf.time - fromTime) < 0.05);
    if (!kfToCopy) return state;
    let targetTime = snapTimeToGrid(toTime, state);
    targetTime = Math.max(0, Math.min(state.duration, targetTime));
    trackTimelineEvent('keyframe_copied', { from_time: fromTime, to_time: targetTime });
    let newKeyframes = state.keyframes.filter(kf => Math.abs(kf.time - targetTime) >= 0.05);
    newKeyframes.push({ ...kfToCopy, time: targetTime });
    newKeyframes.sort((a, b) => a.time - b.time);
    return { keyframes: newKeyframes, currentTime: targetTime, hasUnsavedSequenceChanges: true, history: [...state.history.slice(-49), pushHistory(state, 'Copy Keyframe')], redoStack: [] };
  }),

  moveMultipleKeyframes: (times: number[], delta: number) => set((state: DanceState) => {
    const kfsToMove = state.keyframes.filter(kf => times.some(t => Math.abs(t - kf.time) < 0.05));
    if (kfsToMove.length === 0) return state;

    const movedKfs = kfsToMove.map(kf => {
      let targetTime = snapTimeToGrid(kf.time + delta, state);
      targetTime = Math.max(0, Math.min(state.duration, targetTime));
      return { ...kf, time: targetTime };
    });

    const newTimes = movedKfs.map(kf => kf.time);
    
    let newKeyframes = state.keyframes.filter(kf => 
      !times.some(t => Math.abs(t - kf.time) < 0.05) && 
      !newTimes.some(nt => Math.abs(nt - kf.time) < 0.05)
    );

    newKeyframes = [...newKeyframes, ...movedKfs];
    newKeyframes.sort((a, b) => a.time - b.time);

    const newSelectedFrameTimes = state.selectedFrameTimes.map(st => {
      const isMoved = times.some(t => Math.abs(t - st) < 0.05);
      if (isMoved) {
        let targetTime = snapTimeToGrid(st + delta, state);
        return Math.max(0, Math.min(state.duration, targetTime));
      }
      return st;
    });

    let newCurrentTime = state.currentTime;
    const isCurrentTimeMoved = times.some(t => Math.abs(t - state.currentTime) < 0.05);
    if (isCurrentTimeMoved) {
      newCurrentTime = Math.max(0, Math.min(state.duration, snapTimeToGrid(state.currentTime + delta, state)));
    }

    return { 
      keyframes: newKeyframes, 
      selectedFrameTimes: newSelectedFrameTimes,
      currentTime: newCurrentTime,
      hasUnsavedSequenceChanges: true, 
      history: [...state.history.slice(-49), pushHistory(state, 'Move Multiple Keyframes')], 
      redoStack: [] 
    };
  }),

  copyMultipleKeyframes: (times: number[], delta: number) => set((state: DanceState) => {
    const kfsToCopy = state.keyframes.filter(kf => times.some(t => Math.abs(t - kf.time) < 0.05));
    if (kfsToCopy.length === 0) return state;

    const copiedKfs = kfsToCopy.map(kf => {
      let targetTime = snapTimeToGrid(kf.time + delta, state);
      targetTime = Math.max(0, Math.min(state.duration, targetTime));
      return { ...kf, time: targetTime };
    });

    const newTimes = copiedKfs.map(kf => kf.time);

    let newKeyframes = state.keyframes.filter(kf => 
      !newTimes.some(nt => Math.abs(nt - kf.time) < 0.05)
    );

    newKeyframes = [...newKeyframes, ...copiedKfs];
    newKeyframes.sort((a, b) => a.time - b.time);

    const newSelectedFrameTimes = copiedKfs.map(kf => kf.time);

    let newCurrentTime = state.currentTime;
    const isCurrentTimeCopied = times.some(t => Math.abs(t - state.currentTime) < 0.05);
    if (isCurrentTimeCopied) {
      newCurrentTime = Math.max(0, Math.min(state.duration, snapTimeToGrid(state.currentTime + delta, state)));
    }

    return { 
      keyframes: newKeyframes, 
      selectedFrameTimes: newSelectedFrameTimes,
      currentTime: newCurrentTime,
      hasUnsavedSequenceChanges: true, 
      history: [...state.history.slice(-49), pushHistory(state, 'Copy Multiple Keyframes')], 
      redoStack: [] 
    };
  }),

  rippleMoveMultipleKeyframes: (times: number[], insertAt: number) => set((state: DanceState) => {
    const kfsToMove = state.keyframes.filter(kf => times.some(t => Math.abs(t - kf.time) < 0.05));
    if (kfsToMove.length === 0) return state;

    kfsToMove.sort((a, b) => a.time - b.time);
    const interval = state.metronomeInterval || 0.5;
    const count = kfsToMove.length;
    const shiftAmount = count * interval;

    const isSource = (t: number) => times.some(st => Math.abs(st - t) < 0.05);
    
    const keyframesAfterCut = state.keyframes
      .filter(kf => !isSource(kf.time))
      .map(kf => {
        const shiftLeftCount = times.filter(st => st < kf.time - 0.01).length;
        return { ...kf, time: kf.time - shiftLeftCount * interval };
      });

    const markersAfterCut: Record<string, number[]> = {};
    Object.entries(state.frameMarkers).forEach(([tStr, dots]) => {
      const t = parseFloat(tStr);
      if (!isSource(t)) {
        const shiftLeftCount = times.filter(st => st < t - 0.01).length;
        markersAfterCut[(t - shiftLeftCount * interval).toFixed(3)] = dots;
      }
    });

    const adjustedInsertAt = insertAt - times.filter(st => st < insertAt - 0.01).length * interval;

    const shiftedKeyframes = keyframesAfterCut.map(kf => {
      if (kf.time >= adjustedInsertAt - 0.01) {
        return { ...kf, time: kf.time + shiftAmount };
      }
      return kf;
    });

    const shiftedMarkers: Record<string, number[]> = {};
    Object.entries(markersAfterCut).forEach(([tStr, dots]) => {
      const t = parseFloat(tStr);
      if (t >= adjustedInsertAt - 0.01) {
        shiftedMarkers[(t + shiftAmount).toFixed(3)] = dots;
      } else {
        shiftedMarkers[tStr] = dots;
      }
    });

    const baseSourceTime = kfsToMove[0].time;
    const insertedKeyframes = kfsToMove.map(kf => ({
      ...kf,
      time: adjustedInsertAt + (kf.time - baseSourceTime)
    }));

    const finalKeyframes = [...shiftedKeyframes, ...insertedKeyframes].sort((a, b) => a.time - b.time);

    const movedMarkers: Record<string, number[]> = {};
    times.forEach(t => {
      const dots = state.frameMarkers[t.toFixed(3)];
      if (dots) {
        const relativeOffset = t - baseSourceTime;
        const newTime = adjustedInsertAt + relativeOffset;
        movedMarkers[newTime.toFixed(3)] = dots;
      }
    });

    const finalMarkers = { ...shiftedMarkers, ...movedMarkers };
    const newSelectedFrameTimes = kfsToMove.map(kf => adjustedInsertAt + (kf.time - baseSourceTime));

    return {
      keyframes: finalKeyframes,
      frameMarkers: finalMarkers,
      selectedFrameTimes: newSelectedFrameTimes,
      currentTime: adjustedInsertAt,
      hasUnsavedSequenceChanges: true,
      history: [...state.history.slice(-49), pushHistory(state, 'Ripple Move Keyframes')],
      redoStack: []
    };
  }),

  rippleCopyMultipleKeyframes: (times: number[], insertAt: number) => set((state: DanceState) => {
    const kfsToCopy = state.keyframes.filter(kf => times.some(t => Math.abs(t - kf.time) < 0.05));
    if (kfsToCopy.length === 0) return state;

    kfsToCopy.sort((a, b) => a.time - b.time);
    const interval = state.metronomeInterval || 0.5;
    const count = kfsToCopy.length;
    const shiftAmount = count * interval;

    const shiftedKeyframes = state.keyframes.map(kf => {
      if (kf.time >= insertAt - 0.01) {
        return { ...kf, time: kf.time + shiftAmount };
      }
      return kf;
    });

    const shiftedMarkers: Record<string, number[]> = {};
    Object.entries(state.frameMarkers).forEach(([tStr, dots]) => {
      const t = parseFloat(tStr);
      if (t >= insertAt - 0.01) {
        shiftedMarkers[(t + shiftAmount).toFixed(3)] = dots;
      } else {
        shiftedMarkers[tStr] = dots;
      }
    });

    const baseSourceTime = kfsToCopy[0].time;
    const insertedKeyframes = kfsToCopy.map(kf => ({
      ...kf,
      time: insertAt + (kf.time - baseSourceTime)
    }));

    const finalKeyframes = [...shiftedKeyframes, ...insertedKeyframes].sort((a, b) => a.time - b.time);

    const copiedMarkers: Record<string, number[]> = {};
    times.forEach(t => {
      const dots = state.frameMarkers[t.toFixed(3)];
      if (dots) {
        const relativeOffset = t - baseSourceTime;
        const newTime = insertAt + relativeOffset;
        copiedMarkers[newTime.toFixed(3)] = dots;
      }
    });

    const finalMarkers = { ...shiftedMarkers, ...copiedMarkers };
    const newSelectedFrameTimes = kfsToCopy.map(kf => insertAt + (kf.time - baseSourceTime));

    return {
      keyframes: finalKeyframes,
      frameMarkers: finalMarkers,
      selectedFrameTimes: newSelectedFrameTimes,
      duration: state.duration + shiftAmount,
      currentTime: insertAt,
      hasUnsavedSequenceChanges: true,
      history: [...state.history.slice(-49), pushHistory(state, 'Ripple Copy Keyframes')],
      redoStack: []
    };
  }),

  copyPose: () => set((state: DanceState) => ({ copiedPose: { ...state.currentPose } })),
  pastePoseAndSave: (cameraState, explicitTime) => set((state: DanceState) => {
    if (!state.copiedPose) return state;
    const targetTime = explicitTime !== undefined ? explicitTime : snapTimeToGrid(state.currentTime, state);
    const existingIndex = state.keyframes.findIndex(kf => Math.abs(kf.time - targetTime) < 0.05);
    const existingKf = existingIndex >= 0 ? state.keyframes[existingIndex] : null;
    const newKf: Keyframe = { 
      time: targetTime, 
      pose: { ...state.copiedPose },
      cameraState: cameraState || state.lastCameraState || undefined,
      note: existingKf?.note
    };
    let newKeyframes;
    if (existingIndex >= 0) {
      newKeyframes = [...state.keyframes];
      newKeyframes[existingIndex] = newKf;
    } else {
      newKeyframes = [...state.keyframes, newKf];
    }
    newKeyframes.sort((a, b) => a.time - b.time);
    return { currentPose: { ...state.copiedPose }, keyframes: newKeyframes, currentTime: targetTime, hasUnsavedChanges: false, hasUnsavedSequenceChanges: true, history: [...state.history.slice(-49), pushHistory(state, 'Paste Pose')], redoStack: [] };
  }),

  clearTimeline: () => set((state: DanceState) => {
    trackTimelineEvent('erased');
    const isActuallyModified = state.keyframes.length > 0 || Object.keys(state.frameMarkers).length > 0 || state.footMarkers.length > 0 || state.locationMarkers.length > 0;
    return {
      keyframes: [],
      frameMarkers: {},
      currentTime: 0,
      isPlaying: false,
      currentPose: { ...defaultPose },
      lastCameraState: null,
      hasUnsavedChanges: false,
      hasUnsavedSequenceChanges: state.hasUnsavedSequenceChanges || isActuallyModified,
      footMarkers: [],
      locationMarkers: [],
      selectedFootMarkerLabel: null,
      selectedLocationMarkerLabel: null,
      selectedFrameTimes: [],
      actingSource: { type: 'timeline', time: 0 },
      history: [...state.history.slice(-49), pushHistory(state, 'Clear Timeline')],
      redoStack: []
    };
  }),

  loadSequence: (sequenceData: { id?: string, name?: string, userId?: string, collaborators?: string[], keyframes?: Keyframe[], duration?: number, frameMarkers?: Record<string, number[]>, footMarkers?: { position: [number, number, number], rotation: number, label: number, side: 'left' | 'right' }[], locationMarkers?: { position: [number, number, number], label: number }[], metronomeInterval?: number, beatSubdivision?: number }) => {
    if (sequenceData.name) {
      trackSequenceEvent('loaded', { sequence_name: sequenceData.name });
    }
    const sortedKeyframes = [...(sequenceData.keyframes || [])].sort((a, b) => a.time - b.time);
    set((state: DanceState) => ({
      keyframes: sortedKeyframes,
      frameMarkers: sequenceData.frameMarkers || {},
      footMarkers: sequenceData.footMarkers || [],
      locationMarkers: sequenceData.locationMarkers || [],
      duration: sequenceData.duration || 10,
      metronomeInterval: sequenceData.metronomeInterval !== undefined ? sequenceData.metronomeInterval : state.metronomeInterval,
      beatSubdivision: sequenceData.beatSubdivision !== undefined ? sequenceData.beatSubdivision : state.beatSubdivision,
      currentTime: 0,
      isPlaying: false,
      hasUnsavedChanges: false,
      hasUnsavedSequenceChanges: false,
      selectedFrameTimes: [],
      selectedFootMarkerLabel: null,
      selectedLocationMarkerLabel: null,
      loadedSequence: (sequenceData.id && sequenceData.name) ? { id: sequenceData.id, name: sequenceData.name, userId: sequenceData.userId, collaborators: sequenceData.collaborators } : null
    }));
  },

  groundDancer: () => set((state: DanceState) => {
    const pose = state.currentPose;
    const targetElevation = calculateGroundedElevation(pose);
    return {
      currentPose: {
        ...pose,
        elevation: [pose.elevation?.[0] || 0, targetElevation, pose.elevation?.[2] || 0]
      },
      hasUnsavedChanges: true
    };
  }),

  loadMusic: async (file: File, clearTimeline: boolean = false, source: 'file' | 'url' = 'file', sourceUrl?: string) => {
    set({ isAnalyzing: true, audioHash: null });
    try {
      const audioBuffer = await decodeAudioFile(file);
      const audioHash = await calculateFileHash(file);
      const analysis = await analyzeAudio(audioBuffer);
      const audioUrl = URL.createObjectURL(file);
      const beatInterval = analysis.bpm > 0 ? 60 / analysis.bpm : 0.5;
      const oldUrl = get().audioUrl;
      if (oldUrl) URL.revokeObjectURL(oldUrl);

      const state = get();
      const newDuration = clearTimeline
        ? Math.ceil(analysis.duration)
        : Math.max(Math.ceil(analysis.duration), state.duration);

      const nextMetronomeInterval = Number(beatInterval.toFixed(3));

      let finalKeyframes: Keyframe[] = [];
      let finalFrameMarkers: Record<string, number[]> = {};

      if (clearTimeline) {
        finalKeyframes = [];
        finalFrameMarkers = {};
      } else {
        const tempMusicState = {
          hasMusicLoaded: true,
          beats: analysis.beats,
          metronomeInterval: nextMetronomeInterval,
          beatSubdivision: state.beatSubdivision || 1,
          duration: newDuration
        };

        const snappedKeyframes = state.keyframes.map(kf => ({
          ...kf,
          time: snapTimeToGrid(kf.time, tempMusicState)
        }));

        const uniqueMap = new Map<string, Keyframe>();
        snappedKeyframes.forEach(kf => {
          if (kf.time <= newDuration) {
            uniqueMap.set(kf.time.toFixed(3), kf);
          }
        });
        finalKeyframes = Array.from(uniqueMap.values()).sort((a, b) => a.time - b.time);

        Object.entries(state.frameMarkers).forEach(([timeStr, dots]) => {
          const t = parseFloat(timeStr);
          const snappedT = snapTimeToGrid(t, tempMusicState);
          if (snappedT <= newDuration) {
            const key = snappedT.toFixed(3);
            const existing = finalFrameMarkers[key] || [];
            finalFrameMarkers[key] = Array.from(new Set([...existing, ...dots]));
          }
        });
      }

      set({
        audioFile: file,
        audioFileName: file.name,
        audioUrl,
        audioDuration: analysis.duration,
        beats: analysis.beats,
        measures: analysis.measures,
        bpm: analysis.bpm,
        waveformData: analysis.waveformData,
        hasMusicLoaded: true,
        audioHash,
        audioSourceUrl: sourceUrl ?? null,
        frameMarkers: finalFrameMarkers,
        isAnalyzing: false,
        duration: newDuration,
        metronomeInterval: nextMetronomeInterval,
        keyframes: finalKeyframes,
        currentTime: 0,
        isPlaying: false,
        history: clearTimeline
          ? []
          : [...state.history.slice(-49), pushHistory(state, 'Load Soundtrack')],
        hasUnsavedChanges: false,
        hasUnsavedSequenceChanges: true,
        selectedFrameTimes: [],
        actingSource: { type: 'timeline', time: 0 }
      });
      trackTimelineEvent('soundtrack_loaded', {
        soundtrack_name: file.name,
        duration: analysis.duration,
        clear_timeline: clearTimeline,
        source,
      });
    } catch (err) {
      console.error('Failed to analyze audio:', err);
      set({ isAnalyzing: false });
      throw err;
    }
  },

  downloadAndValidateMusic: async (url: string, expectedHash?: string | null, expectedFileName?: string | null) => {
    // 1. Get cryptographic signature from backend
    const { signature, expires } = await fetchWithAuth('/api/proxy/sign', {
      method: 'POST',
      body: JSON.stringify({ url }),
    });

    // 2. Fetch using proxy with signature parameters
    const response = await fetch(
      `${PROXY_URL}?url=${encodeURIComponent(url)}&expires=${expires}&signature=${signature}`
    );
    if (response.status === 413) throw new Error('LIMIT_EXCEEDED');
    if (response.status === 403) throw new Error('ACCESS_DENIED');
    if (response.status === 404) throw new Error('NOT_FOUND');
    if (!response.ok) throw new Error(`HTTP_${response.status}`);

    const blob = await response.blob();
    const fileName = expectedFileName || extractFilename(url);
    const file = new File([blob], fileName, { type: blob.type || 'audio/mpeg' });

    // 3. Validate that it is a valid audio file by decoding it
    try {
      await decodeAudioFile(file);
    } catch (decodeErr) {
      throw new Error('INVALID_AUDIO_FORMAT');
    }

    // 4. Verify the fetched content matches the saved hash (if any)
    if (expectedHash) {
      const fetchedHash = await calculateFileHash(file);
      if (fetchedHash !== expectedHash) {
        throw new Error('HASH_MISMATCH');
      }
    }

    return file;
  },

  removeMusic: () => {
    const url = get().audioUrl;
    if (url) URL.revokeObjectURL(url);
    set({
      audioFile: null,
      audioFileName: null,
      audioUrl: null,
      audioDuration: null,
      beats: [],
      measures: [],
      bpm: null,
      waveformData: [],
      hasMusicLoaded: false,
      audioHash: null,
      audioSourceUrl: null,
      isAnalyzing: false,
      metronomeInterval: 0.5,
      hasUnsavedSequenceChanges: true
    });
  },

  setMusicVolume: (v: number) => set({ musicVolume: Math.max(0, Math.min(1, v)) }),

  generateRandomPoseWorkspace: () => set((state: DanceState) => {
    const randomPose = generateRandomPose(state.currentPose, state.isMirroring, state.randomnessAmount);
    trackTimelineEvent('dice_pose_created', { randomness_amount: state.randomnessAmount });
    return {
      currentPose: randomPose,
      groundedElevation: calculateGroundedElevation(randomPose),
      hasUnsavedChanges: true,
      history: [...state.history.slice(-49), pushHistory(state, 'Random Pose')],
      redoStack: []
    };
  }),

  mirrorCurrentPose: () => set((state: DanceState) => {
    trackEditorEvent('mirror_pose_flipped');
    const pose = state.currentPose;
    const newPose = { ...pose };
    const processed = new Set<string>();
    Object.keys(pose).forEach(joint => {
      if (processed.has(joint)) return;
      if (joint === 'elevation' || joint === 'orientation' || joint === 'notes') {
        processed.add(joint);
        return;
      }
      
      if (MIRROR_MAP[joint]) {
        const mirrorJoint = MIRROR_MAP[joint];
        const leftVal = (pose[joint] as JointRotation) || [0,0,0];
        const rightVal = (pose[mirrorJoint] as JointRotation) || [0,0,0];
        newPose[joint] = [rightVal[0], -rightVal[1], -rightVal[2]];
        newPose[mirrorJoint] = [leftVal[0], -leftVal[1], -leftVal[2]];
        processed.add(joint);
        processed.add(mirrorJoint);
      } else if (Array.isArray(pose[joint])) {
        const val = pose[joint] as JointRotation;
        newPose[joint] = [val[0], -val[1], -val[2]];
        processed.add(joint);
      }
    });
    return { currentPose: newPose, history: [...state.history.slice(-49), pushHistory(state, 'Mirror Pose')], hasUnsavedChanges: true };
  }),
  
  resetJoint: (jointName: string) => set((state: DanceState) => {
    trackEditorEvent('joint_reset', { joint: jointName });
    const newPose = { ...state.currentPose };
    const defaultVal = (INITIAL_POSE[jointName] as JointRotation) || [0, 0, 0];
    newPose[jointName] = [...defaultVal];

    if (state.isMirroring && MIRROR_MAP[jointName]) {
      const mirrorJoint = MIRROR_MAP[jointName];
      const mirrorDefault = (INITIAL_POSE[mirrorJoint] as JointRotation) || [0, 0, 0];
      newPose[mirrorJoint] = [...mirrorDefault];
    }

    if (jointName === 'torso') {
      newPose.pelvis = [0, 0, 0];
      newPose.stomach = [0, (newPose.stomach as number[])[1], 0];
      newPose.chest = [(newPose.chest as number[])[0], (newPose.chest as number[])[1], 0];
      newPose.neck = [(newPose.neck as number[])[0], (newPose.neck as number[])[1], 0];
      newPose.leftHip = [0, (newPose.leftHip as number[])[1], 0];
      newPose.rightHip = [0, (newPose.rightHip as number[])[1], 0];
      newPose.leftKnee = [0, (newPose.leftKnee as number[])[1], (newPose.leftKnee as number[])[2]];
      newPose.rightKnee = [0, (newPose.rightKnee as number[])[1], (newPose.rightKnee as number[])[2]];
      newPose.leftFoot = [0, (newPose.leftFoot as number[])[1], 0];
      newPose.rightFoot = [0, (newPose.rightFoot as number[])[1], 0];
      newPose.elevation = [0, calculateGroundedElevation(newPose as Pose), 0];
    }

    return {
      currentPose: newPose,
      hasUnsavedChanges: true,
      history: [...state.history.slice(-49), pushHistory(state, 'Reset Joint')]
    };
  }),

  copyJointToOpposite: (jointName: string) => set((state: DanceState) => {
    if (!MIRROR_MAP[jointName]) return state;
    const mirrorJoint = MIRROR_MAP[jointName];
    const val = (state.currentPose[jointName] as JointRotation) || [0, 0, 0];
    const newPose = { ...state.currentPose };
    
    const limits = JOINT_CONSTRAINTS[mirrorJoint];
    let mx = val[0];
    let my = -val[1];
    let mz = -val[2];
    
    if (limits) {
      mx = clamp(mx, limits.x[0], limits.x[1]);
      my = clamp(my, limits.y[0], limits.y[1]);
      mz = clamp(mz, limits.z[0], limits.z[1]);
    }
    
    newPose[mirrorJoint] = [mx, my, mz];
    
    return { 
      currentPose: newPose, 
      hasUnsavedChanges: true,
      history: [...state.history.slice(-49), pushHistory(state, 'Copy Joint to Opposite')]
    };
  }),

  copyJointFromOpposite: (jointName: string) => set((state: DanceState) => {
    if (!MIRROR_MAP[jointName]) return state;
    const mirrorJoint = MIRROR_MAP[jointName];
    const val = (state.currentPose[mirrorJoint] as JointRotation) || [0, 0, 0];
    const newPose = { ...state.currentPose };
    
    const limits = JOINT_CONSTRAINTS[jointName];
    let mx = val[0];
    let my = -val[1];
    let mz = -val[2];
    
    if (limits) {
      mx = clamp(mx, limits.x[0], limits.x[1]);
      my = clamp(my, limits.y[0], limits.y[1]);
      mz = clamp(mz, limits.z[0], limits.z[1]);
    }
    
    newPose[jointName] = [mx, my, mz];
    
    return { 
      currentPose: newPose, 
      hasUnsavedChanges: true,
      history: [...state.history.slice(-49), pushHistory(state, 'Copy Joint from Opposite')]
    };
  }),

  reflectJoint: () => set((state: DanceState) => {
    const joint = state.selectedJoint;
    if (!joint || !MIRROR_MAP[joint]) return state;
    trackEditorEvent('joint_reflected', { joint });
    
    const mirrorJoint = MIRROR_MAP[joint];
    const val = (state.currentPose[joint] as JointRotation) || [0, 0, 0];
    const newPose = { ...state.currentPose };
    
    // Symmetric mirror calculation
    const limits = JOINT_CONSTRAINTS[mirrorJoint];
    let mx = val[0];
    let my = -val[1];
    let mz = -val[2];
    
    if (limits) {
      mx = Math.max(limits.x[0], Math.min(limits.x[1], mx));
      my = Math.max(limits.y[0], Math.min(limits.y[1], my));
      mz = Math.max(limits.z[0], Math.min(limits.z[1], mz));
    }
    
    newPose[mirrorJoint] = [mx, my, mz];
    
    return { 
      currentPose: newPose, 
      hasUnsavedChanges: true,
      history: [...state.history.slice(-49), pushHistory(state, 'Reflect Joint')]
    };
  }),

  toggleFrameMarker: (time: number, dotIndex: number) => set((state: DanceState) => {
    if (dotIndex === 1 || dotIndex === 2) return state;
    const key = time.toFixed(3);
    const existingMarkers = state.frameMarkers[key] || [];
    let newMarkers = existingMarkers.includes(dotIndex) ? existingMarkers.filter(i => i !== dotIndex) : [...existingMarkers, dotIndex];
    const newFrameMarkers = { ...state.frameMarkers };
    if (newMarkers.length === 0) delete newFrameMarkers[key]; else newFrameMarkers[key] = newMarkers;
    return { frameMarkers: newFrameMarkers, hasUnsavedSequenceChanges: true };
  }),

  confirmAction: (options) => set({ confirmDialog: { ...options, isOpen: true } }),
  closeConfirm: () => set({ confirmDialog: null }),
  setConfirmReset: (v: boolean) => {
    trackEditorEvent('confirm_reset_toggled', { enabled: v });
    set({ confirmReset: v });
    useStorageStore.getState().updatePreference('confirmReset', v);
  },
  
  openInputDialog: (options) => set({ inputDialog: { isOpen: true, ...options } }),
  closeInputDialog: () => set({ inputDialog: null }),

  loadPose: (pose: Pose, cameraState?: CameraState, source?: ActingSource) => {
    if (source && source.type === 'library' && source.name) {
      trackPoseEvent('loaded', { pose_name: source.name });
    }
    set({
      currentPose: { ...pose },
      groundedElevation: calculateGroundedElevation(pose),
      lastCameraState: cameraState || get().lastCameraState,
      history: [],
      hasUnsavedChanges: false,
      actingSource: source || { type: 'none' }
    });
  },

  applyPoseToTime: (pose: Pose, time: number, cameraState?: CameraState) => {
    trackTimelineEvent('pose_saved_to_keyframe', { keyframe: time });
    set((state: DanceState) => {
      const newKfIdx = state.keyframes.findIndex(kf => Math.abs(kf.time - time) < 0.05);
      const newKf: Keyframe = {
        time,
        pose: { ...pose },
        cameraState: cameraState || state.lastCameraState || undefined
      };
      
      let newKeyframes;
      if (newKfIdx >= 0) {
        // Merge rather than replace — overwriting only the pose/cameraState
        // must preserve the existing keyframe's own fields (e.g. its Frame
        // Note), which a full replacement would otherwise silently drop.
        newKeyframes = [...state.keyframes];
        newKeyframes[newKfIdx] = { ...newKeyframes[newKfIdx], ...newKf };
      } else {
        newKeyframes = [...state.keyframes, newKf];
      }
      newKeyframes.sort((a, b) => a.time - b.time);

      return {
        currentPose: { ...pose },
        groundedElevation: calculateGroundedElevation(pose),
        keyframes: newKeyframes,
        currentTime: time,
        hasUnsavedChanges: false,
        hasUnsavedSequenceChanges: true,
        actingSource: { type: 'timeline', time },
        history: [],
        lastCameraState: cameraState || state.lastCameraState
      };
    });
  },

  newSequence: () => {
    trackEvent('new_sequence_started');
    get().clearTimeline();
    get().removeMusic();
    set({ duration: 10, metronomeInterval: 0.5, loadedSequence: null, hasUnsavedSequenceChanges: false, footMarkers: [] });
  },

  exportSequence: () => {
    const state = get();
    trackEvent('sequence_exported', { soundtrack_name: state.hasMusicLoaded ? state.audioFileName : null });
    const data: any = {
      version: 1,
      type: 'sequence',
      duration: state.duration,
      keyframes: state.keyframes,
      frameMarkers: state.frameMarkers,
      metronomeInterval: state.metronomeInterval,
    };
    if (state.hasMusicLoaded) {
      data.music = {
        fileName: state.audioFileName,
        bpm: state.bpm,
        beats: state.beats,
        measures: state.measures,
        hash: state.audioHash,
        sourceUrl: state.audioSourceUrl,
      };
    }
    const name = state.loadedSequence?.name || 'choreography';
    exportData(data, `${name.replace(/\s+/g, '_')}.cseq`);
  },

  importSequence: async (file: File) => {
    try {
      const data = await importData<any>(file);
      if (data.type === 'sequence' || data.keyframes) {
        get().loadSequence(data);
        trackEvent('sequence_imported', { success: true, source: 'file_upload' });
        get().setActiveModule('PoseDesigner');
        if (data.music) {
          await handleMusicAutoLoad(data.music, set, get);
        }
      } else {
        throw new Error('Invalid sequence file');
      }
    } catch (err: any) {
      trackEvent('sequence_imported', { success: false, error: err?.message || 'unknown' });
      get().confirmAction({
        title: "Import Error",
        message: "Could not load sequence: Invalid file format.",
        isAlert: true,
        onConfirm: () => {}
      });
    }
  },

  exportPose: () => {
    trackEvent('pose_exported');
    const state = get();
    const data = {
      version: 1,
      type: 'pose',
      pose: state.currentPose,
      cameraState: state.lastCameraState,
    };
    const timestamp = new Date().getTime();
    exportData(data, `pose_${timestamp}.cpos`);
  },

  importPose: async (file: File) => {
    try {
      const data = await importData<any>(file);
      if (data.type === 'pose' || (data.pose && !data.keyframes)) {
        get().loadPose(data.pose, data.cameraState);
        trackEvent('pose_imported', { success: true, source: 'file_upload' });
      } else {
        throw new Error('Invalid pose file');
      }
    } catch (err: any) {
      trackEvent('pose_imported', { success: false, error: err?.message || 'unknown' });
      get().confirmAction({
        title: "Import Error",
        message: "Could not load pose: Invalid file format.",
        isAlert: true,
        onConfirm: () => {}
      });
    }
  },

  importFile: async (file: File) => {
    try {
      const data = await importData<any>(file);
      if (data.type === 'sequence' || data.keyframes) {
        get().loadSequence(data);
        trackEvent('sequence_imported', { success: true, source: 'file_upload' });
        get().setActiveModule('PoseDesigner');
        if (data.music) {
          await handleMusicAutoLoad(data.music, set, get);
        }
      } else if (data.type === 'pose' || (data.pose && !data.keyframes)) {
        get().loadPose(data.pose, data.cameraState);
        trackEvent('pose_imported', { success: true, source: 'file_upload' });
      } else {
        throw new Error('Invalid choreographer file format');
      }
    } catch (err: any) {
      trackEvent('file_import_failed', { error: err?.message || 'unknown' });
      get().confirmAction({
        title: "Import Error",
        message: "Could not load file: Invalid format or corrupted data.",
        isAlert: true,
        onConfirm: () => {}
      });
    }
  },


  setAutoSaveEnabled: (v: boolean) => set((state: DanceState) => {
    trackTimelineEvent('autosave_toggled', { enabled: v });
    const next: Partial<DanceState> = { autoSaveEnabled: v };
    if (state.confirmDialog && state.confirmDialog.showCheckbox) {
      next.confirmDialog = { ...state.confirmDialog, checkboxValue: v };
    }
    useStorageStore.getState().updatePreference('autoSaveEnabled', v);
    return next;
  }),

  // Multi-frame selection and context menu
  toggleFrameSelection: (time, multi) => set((state: DanceState) => {
    if (multi) {
      const isSelected = state.selectedFrameTimes.some(st => Math.abs(st - time) < 0.05);
      const times = isSelected
        ? state.selectedFrameTimes.filter(st => Math.abs(st - time) >= 0.05)
        : [...state.selectedFrameTimes, time];
      return { selectedFrameTimes: times };
    } else {
      return { selectedFrameTimes: [time] };
    }
  }),
  deleteSelectedFrames: () => set((state: DanceState) => {
    const times = state.selectedFrameTimes.length > 0 ? [...state.selectedFrameTimes] : [state.currentTime];
    times.sort((a, b) => a - b);
    
    const interval = state.metronomeInterval || 0.5;
    const isSelected = (t: number) => times.some(st => Math.abs(st - t) < 0.05);
    
    // Shift keyframes
    const newKeyframes: Keyframe[] = [];
    state.keyframes.forEach(kf => {
      if (!isSelected(kf.time)) {
        const shiftCount = times.filter(st => st < kf.time - 0.01).length;
        newKeyframes.push({ ...kf, time: kf.time - shiftCount * interval });
      }
    });

    // Shift markers
    const newFrameMarkers: Record<string, number[]> = {};
    Object.entries(state.frameMarkers).forEach(([tStr, dots]) => {
      const t = parseFloat(tStr);
      if (!isSelected(t)) {
        const shiftCount = times.filter(st => st < t - 0.01).length;
        const newT = t - shiftCount * interval;
        newFrameMarkers[newT.toFixed(3)] = dots;
      }
    });

    const totalShift = times.length * interval;
    const newDuration = Math.max(interval, state.duration - totalShift);
    
    return { 
      keyframes: newKeyframes, 
      frameMarkers: newFrameMarkers, 
      selectedFrameTimes: [],
      duration: newDuration,
      currentTime: Math.max(0, Math.min(state.currentTime, newDuration)),
      hasUnsavedSequenceChanges: true,
      history: [...state.history.slice(-49), pushHistory(state, 'Delete Frames')],
      redoStack: []
    };
  }),
  clearSelectedFrames: () => set((state: DanceState) => {
    const times = state.selectedFrameTimes.length > 0 ? state.selectedFrameTimes : [state.currentTime];
    const newKeyframes = state.keyframes.filter(kf => !times.some(t => Math.abs(t - kf.time) < 0.05));
    const newFrameMarkers = { ...state.frameMarkers };
    times.forEach(t => {
      delete newFrameMarkers[t.toFixed(3)];
    });
    return { 
      keyframes: newKeyframes, 
      frameMarkers: newFrameMarkers, 
      selectedFrameTimes: [],
      hasUnsavedSequenceChanges: true,
      history: [...state.history.slice(-49), pushHistory(state, 'Clear Frames')],
      redoStack: []
    };
  }),
  copySelectedFrames: () => set((state: DanceState) => {
    const times = state.selectedFrameTimes.length > 0 ? state.selectedFrameTimes : [state.currentTime];
    const frames = state.keyframes
      .filter(kf => times.some(t => Math.abs(t - kf.time) < 0.05))
      .map(kf => ({ ...kf }));
    
    if (frames.length === 0) return state;
    
    // Sort copied frames by time
    frames.sort((a, b) => a.time - b.time);
    
    return { copiedFrames: frames };
  }),
  insertCopiedFrames: (before) => set((state: DanceState) => {
    if (state.copiedFrames.length === 0) return state;
    
    const interval = state.metronomeInterval || 0.5;
    const pivotTime = state.currentTime;
    const count = state.copiedFrames.length;
    const shiftAmount = count * interval;
    
    // Determine insertion start time
    const insertStartTime = before ? pivotTime : pivotTime + interval;
    
    // Shift existing keyframes and markers
    const shiftedKeyframes = state.keyframes.map(kf => {
      if (kf.time >= insertStartTime - 0.01) {
        return { ...kf, time: kf.time + shiftAmount };
      }
      return kf;
    });
    
    const shiftedMarkers: Record<string, number[]> = {};
    Object.entries(state.frameMarkers).forEach(([tStr, dots]) => {
      const t = parseFloat(tStr);
      if (t >= insertStartTime - 0.01) {
        shiftedMarkers[(t + shiftAmount).toFixed(3)] = dots;
      } else {
        shiftedMarkers[tStr] = dots;
      }
    });

    // Create new keyframes from copied ones
    const baseCopyTime = state.copiedFrames[0].time;
    const newFrames = state.copiedFrames.map(kf => ({
      ...kf,
      time: insertStartTime + (kf.time - baseCopyTime)
    }));

    const finalKeyframes = [...shiftedKeyframes, ...newFrames].sort((a, b) => a.time - b.time);
    
    return {
      keyframes: finalKeyframes,
      frameMarkers: shiftedMarkers,
      duration: state.duration + shiftAmount,
      hasUnsavedSequenceChanges: true,
      history: [...state.history.slice(-49), pushHistory(state, 'Insert Copied Frames')],
      redoStack: []
    };
  }),
  insertBlankFrames: (count, before) => set((state: DanceState) => {
    if (count <= 0) return state;
    
    const interval = state.metronomeInterval || 0.5;
    const pivotTime = state.currentTime;
    const shiftAmount = count * interval;
    
    // Determine insertion start time
    const insertStartTime = before ? pivotTime : pivotTime + interval;
    
    // Shift existing keyframes and markers
    const shiftedKeyframes = state.keyframes.map(kf => {
      if (kf.time >= insertStartTime - 0.01) {
        return { ...kf, time: kf.time + shiftAmount };
      }
      return kf;
    });
    
    const shiftedMarkers: Record<string, number[]> = {};
    Object.entries(state.frameMarkers).forEach(([tStr, dots]) => {
      const t = parseFloat(tStr);
      if (t >= insertStartTime - 0.01) {
        shiftedMarkers[(t + shiftAmount).toFixed(3)] = dots;
      } else {
        shiftedMarkers[tStr] = dots;
      }
    });

    return {
      keyframes: shiftedKeyframes,
      frameMarkers: shiftedMarkers,
      duration: state.duration + shiftAmount,
      hasUnsavedSequenceChanges: true,
      history: [...state.history.slice(-49), pushHistory(state, 'Insert Blank Frames')],
      redoStack: []
    };
  }),
  isReadOnly: false,
  setReadOnly: (val) => set((state) => ({
    isReadOnly: val,
    ...(val ? { selectedJoint: null } : {})
  })),
  
  // Direct joint dragging
  isDirectDragging: false,
  directDragJoint: null,
  directDragParent: null,
  directDragStartX: 0,
  directDragStartY: 0,
  directDragStartPos: null,
  startDirectDrag: (joint, parent, clientX, clientY, startPos) => {
    set({
      isDirectDragging: true,
      directDragJoint: joint,
      directDragParent: parent,
      directDragStartX: clientX,
      directDragStartY: clientY,
      directDragStartPos: startPos,
      isPosing: true
    });
    get().commitHistory('Direct Drag Pose');
  },
  endDirectDrag: () => set({
    isDirectDragging: false,
    directDragJoint: null,
    directDragParent: null,
    directDragStartPos: null,
    isPosing: false
  }),

  openContextMenu: (x, y, items, align) => set({ contextMenu: { isOpen: true, x, y, items, align } }),
  closeContextMenu: () => set({ contextMenu: null }),

  shareDialog: null,
  openShareDialog: (sequence) => set({ shareDialog: { isOpen: true, sequence } }),
  closeShareDialog: () => set({ shareDialog: null }),
}));

if (typeof window !== 'undefined') {
  (window as any).useDanceStore = useDanceStore;

  // Sync useStorageStore preferences back to useDanceStore on load / change
  useStorageStore.subscribe((state) => {
    const prefs = state.preferences;
    if (!prefs) return;
    const danceStore = useDanceStore.getState();
    
    let changed = false;
    const updates: any = {};
    
    if (prefs.compactTimeline !== undefined && danceStore.isTimelineCompact !== prefs.compactTimeline) {
      updates.isTimelineCompact = prefs.compactTimeline;
      changed = true;
    }
    if (prefs.showJointLabels !== undefined && danceStore.showLabels !== prefs.showJointLabels) {
      updates.showLabels = prefs.showJointLabels;
      changed = true;
    }
    if (prefs.showJointAxes !== undefined && danceStore.showAxes !== prefs.showJointAxes) {
      updates.showAxes = prefs.showJointAxes;
      changed = true;
    }
    if (prefs.mirrorMode !== undefined && danceStore.isMirroring !== prefs.mirrorMode) {
      updates.isMirroring = prefs.mirrorMode;
      changed = true;
    }
    if (prefs.autoSaveEnabled !== undefined && danceStore.autoSaveEnabled !== prefs.autoSaveEnabled) {
      updates.autoSaveEnabled = prefs.autoSaveEnabled;
      changed = true;
    }
    if (prefs.confirmReset !== undefined && danceStore.confirmReset !== prefs.confirmReset) {
      updates.confirmReset = prefs.confirmReset;
      changed = true;
    }
    
    if (changed) {
      useDanceStore.setState(updates);
    }
  });

  // Apply initial preferences on startup
  const initialPrefs = useStorageStore.getState().preferences;
  if (initialPrefs) {
    const updates: any = {};
    if (initialPrefs.compactTimeline !== undefined) updates.isTimelineCompact = initialPrefs.compactTimeline;
    if (initialPrefs.showJointLabels !== undefined) updates.showLabels = initialPrefs.showJointLabels;
    if (initialPrefs.showJointAxes !== undefined) updates.showAxes = initialPrefs.showJointAxes;
    if (initialPrefs.mirrorMode !== undefined) updates.isMirroring = initialPrefs.mirrorMode;
    if (initialPrefs.autoSaveEnabled !== undefined) updates.autoSaveEnabled = initialPrefs.autoSaveEnabled;
    if (initialPrefs.confirmReset !== undefined) updates.confirmReset = initialPrefs.confirmReset;
    useDanceStore.setState(updates);
  }
}

